/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";

import { useParams } from "react-router-dom";

import { useLocalization } from "@progress/kendo-react-intl";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Error } from "@progress/kendo-react-labels";
import {
  emailValidator,
  requiredValidator,
  passwordMatchValidator,
  address1Validator,
  cityValidator,
  stateValidator,
  zipValidator,
  phoneValidator,
  newPasswordValidator,
  dealerCodeValidator,
  dollarValidator,
  positiveNumberValidator,
} from "../validators";
import raw_timezones from "src/stores/timezones.json";

import { AppContext } from "../AppContext";
import { useServices } from "src/services";
import { TextArea, Input } from "@progress/kendo-react-inputs";
import { DatePicker } from 'src/components/Form/DatePicker';
import { TimePicker } from 'src/components/Form/TimePicker';
import { NumericTextBox } from 'src/components/Form/NumericTextBox';
import { MaskedTextBox } from "src/components/Form/MaskedTextBox";
import { useHistory } from "react-router";
import moment from "moment";
import capitalize from "src/utils/capitalize";
import titleCase, { sentenceCase } from "src/utils/titleCase";
import { normalizeMemo } from "src/utils/normalizeMemo";
import { CANCELLATION_REASONS } from "src/utils/cancellationReasons";
import { ORGANIZATION_TYPES, ORGANIZATION_SUBCATEGORIES, FBT_PARTNERS, organizationTypeLabel, organizationSubcategoryLabel } from "src/utils/approvalOptions";
import DealerFeedbackSection from "src/components/DealerFeedbackSection";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  UploadFileInfo,
  UploadOnAddEvent,
  UploadOnProgressEvent,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from "@progress/kendo-react-upload";
import {
  CompositeFilterDescriptor,
  filterBy,
  FilterDescriptor,
} from "@progress/kendo-data-query";
import { DropDownList } from "src/components/Form/DropDownList";
import { DropDownListFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { Upload } from "src/components/Form/Upload";
import { Dealer, D4URxEvent, UserDetails } from "src/utils/classes";
import { useStores } from "src/stores";

type EventScreenParams = {
  event_id: string;
};

const dealerTypes = [
  { value: "F", text: "Ford" },
  { value: "FL", text: "Ford/Lincoln" },
];

const dealerRegions = [
  { value: "CE", text: "Central Market Area" },
  { value: "EMA", text: "East Market Area" },
  { value: "GL", text: "Great Lakes Market Area" },
  { value: "SE", text: "Southeast Market Area" },
  { value: "W", text: "West Market Area" },
];

const fontBold = { fontWeight: 700 }
const fontSemiBold = { fontWeight: 600 }

const EventDetailScreen = () => {
  const { ...formValues } = React.useContext(AppContext);
  const localizationService = useLocalization();
  const formRef = React.useRef(null);

  const { d4ur } = useServices();
  const { login } = useStores();
  let history = useHistory();
  const { event_id } = useParams<EventScreenParams>();

  const siteDomain: string =
    process.env.REACT_APP_ENV === "staging"
      ? "staging.driveforyourcommunity.com"
      : "driveforyourcommunity.com";

  const [dialogDisplayed, setDialogDisplayed] = useState<string>("none");
  const [selectedEmailType, setEmailType] = useState<string>("");
  const [formDisplayed, setFormDisplayed] = useState<string>("none");
  const [formError, setFormError] = useState<string>();

  const [planners, setPlanners] = useState<UserDetails[]>([]);

  const [thisEvent, setThisEvent] = useState<D4URxEvent>();
  const [customData, setCustomData] = useState<any>({});
  const [addressVerified, toggleAddressVerified] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showRRDMessage, setShowRRDMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [displayOtherAddressForm, setDisplayOtherAddressForm] = useState<boolean>(false);
  const [secondaryOrgDeleted, deleteSecondaryOrg] = useState<boolean>(false);
  const [shareChangesVisible, setShareChangesVisible] = useState<boolean>(false);

  // Banking info (pre-approval memo + check payable)
  const [memoDialogVisible, setMemoDialogVisible] = useState<boolean>(false);
  const [memoText, setMemoText] = useState<string>('');
  const [checkPayableText, setCheckPayableText] = useState<string>('');
  const [preApprovalMemo, setPreApprovalMemo] = useState<string>('');
  const [preApprovalCheckPayable, setPreApprovalCheckPayable] = useState<string>('');

  // Add dealer notes state management
  const [dealerNotesModalVisible, setDealerNotesModalVisible] = useState<boolean>(false);
  const [selectedDealerNotes, setSelectedDealerNotes] = useState<any[]>([]);
  const [selectedDealerNameForNotes, setSelectedDealerNameForNotes] = useState<string>('');
  const [selectedDealerIdForNotes, setSelectedDealerIdForNotes] = useState<number | null>(null);
  
  // Add dealer note modal states
  const [addDealerNoteModalVisible, setAddDealerNoteModalVisible] = useState<boolean>(false);
  const [selectedDealerForNote, setSelectedDealerForNote] = useState<any>(null);
  const [selectedDealerNameForNote, setSelectedDealerNameForNote] = useState<string>('');
  const [newDealerNoteText, setNewDealerNoteText] = useState<string>('');
  const [showAddDealerNoteInModal, setShowAddDealerNoteInModal] = useState<boolean>(false);
  const [newDealerNoteTextInModal, setNewDealerNoteTextInModal] = useState<string>('');
  const [selectedDealerForNoteId, setSelectedDealerForNoteId] = useState<number | null>(null);
  
  // Separate state for dealer notes modal to avoid conflicts
  const [dealerNotesModalDealerId, setDealerNotesModalDealerId] = useState<number | null>(null);
  
  // Edit dealer note states
  const [editingDealerNoteIndex, setEditingDealerNoteIndex] = useState<number | null>(null);
  const [editedDealerNoteText, setEditedDealerNoteText] = useState<string>('');
  const [originalDealerNoteContent, setOriginalDealerNoteContent] = useState<string>('');
  
  // Local notes state for instant display updates
  const [localDealerNotes, setLocalDealerNotes] = useState<any[]>([]);

  // Helper function to update local notes immediately
  const updateLocalNotes = (notes: any[]) => {
    setLocalDealerNotes([...notes]);
  };

  interface IMagic {
    url: string;
    token: string;
  }
  const [magicLink, setMagicLink] = useState<IMagic>();
  const [dealer, setDealer] = useState<Dealer>();
  const [charity, setCharity] = useState<Charity>();
  const [registrants, setRegistrants] = useState<Registrants>();
  // const [assignedPlanner, setAssignedPlanner] = useState<UserDetails>();
  const [timeZones, setTimeZones] = useState<string[]>();

  const [files, setFiles] = React.useState<Array<UploadFileInfo>>([]);
  const onAdd = (event: UploadOnAddEvent) => {
    console.log("onAdd: ", event.affectedFiles);

    setFiles(event.newState);
  };

  const onRemove = (event: UploadOnRemoveEvent) => {
    console.log("onRemove: ", event.affectedFiles);

    setFiles(event.newState);
  };

  const onProgress = (event: UploadOnProgressEvent) => {
    console.log("onProgress: ", event.affectedFiles);

    setFiles(event.newState);
  };

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    console.log("onStatusChange: ", event.affectedFiles);

    setFiles(event.newState);
  };

  const doMagicLink = () => {
    console.log("magicLink", magicLink);
    const { url, token } = magicLink!;
    // Copy the magic link URL to clipboard
    try {
      navigator.clipboard.writeText(`${url}/auto_login/${token}`)
        .then(() => {
          console.log('Magic link URL copied to clipboard', `${url}/auto_login/${token}`);
        })
        .catch(err => {
          console.error('Could not copy text to clipboard: ', err, `${url}/auto_login/${token}`);
        });
    } catch (error) {
      console.error('Clipboard API not available: ', error, `${url}/auto_login/${token}`);
    }
    window.open(`${url}/auto_login/${token}`, "_blank");
  };

  const getRequestData = () => {
    setFormError("");
    setTimeZones(raw_timezones);
    d4ur.getPlanners().then((plannersResult) => {
      // const foundPlanners = (plannersResult as D4URxFetchResult).result as UserDetails[];
      setPlanners((plannersResult as D4URxFetchResult).result as UserDetails[]);
      d4ur.getEventToken(event_id).then((tokenResult) => {
        const { token, url } = (tokenResult as D4URxFetchResult).result;

        setMagicLink({
          token,
          url,
        });
      });

      d4ur.getEvent(event_id).then((ret) => {
        console.log("ret ****", ret);

        const { event, dealer, charity, registrants } = (ret as D4URxFetchResult).result;

        // const { assigned_planner } = JSON.parse(event?.eligibility_custom_data || "{}");

        charity.address_2 = charity.address_2 || "";

        // setAssignedPlanner(
        //   foundPlanners?.find((x) => x.user_id === assigned_planner)
        // );
        // console.log("event!!!", event.status);

        setThisEvent(new D4URxEvent(event));
        setCustomData(JSON.parse(event.event_custom_data || "{}"));
        setDealer(new Dealer(dealer));
        
        setCharity(charity as Charity);
        setRegistrants(registrants as Registrants)
      });
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(getRequestData, []);

  // Sync local dealer notes when dealer data changes
  useEffect(() => {
    if (dealer && dealer.dealer_notes) {
      setLocalDealerNotes(dealer.dealer_notes);
    } else {
      setLocalDealerNotes([]);
    }
  }, [dealer]);

  // Initialize banking info from event spreadsheet_data
  useEffect(() => {
    try {
      const sd = thisEvent?.spreadsheet_data ? JSON.parse(thisEvent.spreadsheet_data) : {};
      setPreApprovalMemo(sd?.pre_approval_memo || '');
      setPreApprovalCheckPayable(sd?.check_payable || '');
    } catch {
      setPreApprovalMemo('');
      setPreApprovalCheckPayable('');
    }
  }, [thisEvent]);

  // Keep modal state in sync with local notes
  useEffect(() => {
    if (dealerNotesModalVisible) {
      setSelectedDealerNotes(localDealerNotes);
    }
  }, [localDealerNotes, dealerNotesModalVisible]);

  // Control body overflow when modals are open
  useEffect(() => {
    if (dealerNotesModalVisible || addDealerNoteModalVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [dealerNotesModalVisible, addDealerNoteModalVisible]);

  const ref: any = React.useRef(null);
  const attendanceRef: any = React.useRef(null);
  const dateRef: any = React.useRef(null);
  const startTimeRef: any = React.useRef(null);
  const endTimeRef: any = React.useRef(null);

  const handleOnWheel = (event: any) => {
    event.stopPropagation();
  };

  React.useEffect(() => {
    ref?.current?.element.addEventListener('wheel', handleOnWheel);
    attendanceRef?.current?.element.addEventListener('wheel', handleOnWheel);
    dateRef?.current?.element.addEventListener('wheel', handleOnWheel);
    startTimeRef?.current?.element.addEventListener('wheel', handleOnWheel);
    endTimeRef?.current?.element.addEventListener('wheel', handleOnWheel);

    return () => {
      ref?.current?.element.removeEventListener('wheel', handleOnWheel);
      attendanceRef?.current?.element.removeEventListener('wheel', handleOnWheel);
      dateRef?.current?.element.removeEventListener('wheel', handleOnWheel);
      startTimeRef?.current?.element.removeEventListener('wheel', handleOnWheel);
      endTimeRef?.current?.element.removeEventListener('wheel', handleOnWheel);
    };
  });

  const onSubmitDealer = React.useCallback(
    async (dataItem) => {
      setFormDisplayed("none");

      if (!dataItem.confirm_password) {
        delete dataItem.password;
      }

      const call: any = await d4ur.updateDealer(dataItem);
      if (call.result?.errors) {
        console.log("[EventDetailScreen]", "Update Dealer Error");
        setFormError(call.result.errors.join("\n"));
      } else {
        console.log("[EventDetailScreen]", "dealer updated");
        history.push('/events');
      }
    },
    [d4ur, history]
  );

  const onSubmitCharity = React.useCallback(
    async (dataItem) => {
      setFormDisplayed("none");

      // Update charity fields
      const call: any = await d4ur.updateCharity(dataItem);
      if (call.result.errors) {
        console.log("[EventDetailScreen]", "Update Charity Error");
        setFormError(call.result.errors.join("\n"));
      } else {
        console.log("[EventDetailScreen]", "charity updated");
        history.push('/events');
      }
    },
    [d4ur, history, thisEvent]
  );

  const onSubmitPlanner = React.useCallback(
    async (dataItem) => {
      console.log("dataItem!!!!!!!!", dataItem);
      d4ur
        .setEligibleEventRequest(
          thisEvent!.event_request_id!,
          dataItem.assigned_planner.user_id
        )
        .then((res) => {
          setDialogDisplayed("none");
          history.push('/events');
        });
    },
    [d4ur, history, thisEvent]
  );

  // Event dates are frozen once the event is over. A dealer's event was edited
  // after it had already run, which left the record in a bad state, so both
  // date-change paths (the Edit Event Plan date field and Reschedule Event) are
  // switched off for past events until the proper fix is scoped.
  const eventDateLocked = React.useMemo(() => {
    if (!thisEvent) return false;
    if (thisEvent.status === 'event ended') return true;
    if (!thisEvent.start_date) return false;
    return moment(thisEvent.start_date).endOf('day').isBefore(moment());
  }, [thisEvent]);

  const onSubmitUpdateEvent = React.useCallback(
    async (dataItem) => {
      if (!displayOtherAddressForm && dataItem.event_location_type && thisEvent?.event_location_type !== 'Other') {
        dataItem.address = '';
        dataItem.city = '';
        dataItem.state = '';
        dataItem.zip_code = '';
      }
      if (!dataItem.event_location_type) {
        dataItem.event_location_type = thisEvent?.event_location_type;
      }
      if (secondaryOrgDeleted) {
        dataItem.secondary_charity = null;
      }

      // The classification dropdowns work in labels; the API takes the stored values (null = unset)
      const orgType = ORGANIZATION_TYPES.find((t) => t.label === dataItem.organization_type_label)?.value || null;
      dataItem.organization_type = orgType;
      dataItem.organization_subcategory = (ORGANIZATION_SUBCATEGORIES[orgType || ''] || [])
        .find((sub) => sub.label === dataItem.organization_subcategory_label)?.value || null;
      dataItem.fbt_partner = dataItem.fbt_status === 'Yes' ? dataItem.fbt_partner || null : null;
      delete dataItem.organization_type_label;
      delete dataItem.organization_subcategory_label;
      delete dataItem.fbt_status;

      // Belt and braces with the disabled date field above: a past event keeps
      // whatever date it already had, whatever the form hands back.
      if (eventDateLocked) {
        dataItem.start_date = thisEvent?.start_date;
      }

      // start and end are the same, need to update both
      dataItem.end_date = dataItem.start_date;

      // Format times properly for API (HH:mm:00 format)
      if (dataItem.start_time) {
        dataItem.start_time = moment(dataItem.start_time).format("HH:mm:00");
      }
      if (dataItem.end_time) {
        dataItem.end_time = moment(dataItem.end_time).format("HH:mm:00");
      }

      try {
        d4ur.updateEvent(dataItem)
        // setShareChangesVisible(true);
      } catch (e) {
        console.log("[EventDetailScreen]", "Update event Error", e);
        setFormError('There was an error updating this event, please contact the development team.');
      }
      setFormDisplayed("none");
      history.push('/events');
    },
    [d4ur, history, displayOtherAddressForm, secondaryOrgDeleted, thisEvent, eventDateLocked]
  );

  const onSubmitSendEmails = React.useCallback(
    async (dataItem) => {
      setIsLoading(true);
      setSuccessMessage('');
      setErrorMessage('');
      // todo: type ret object
      d4ur.triggerEmail(dataItem.email_type.id, thisEvent?.event_id, dataItem.send_to).then((ret: any) => {
        // console.log('triggerEmail event ret:', ret)
        if (ret.response.status === 200) {
          formRef.current?.resetForm();
          setIsLoading(false);
          setEmailType('');
          setSuccessMessage('Successfully sent emails!')
        } else {
          setErrorMessage(ret.result.message)
          setIsLoading(false);
        }
        return;
      })
        .catch(e => console.log('trigger emaik error:', e))
    },
    [d4ur, history, thisEvent]
  );

  const generateEmailHistorySummary = () => {
    const emailHistory: Array<{ type: string; sent_at: string; sent_to: string[] }> = [];
    
    // Pre-Event (training_reminder_sent)
    const trainingReminders = thisEvent?.formatted_custom_data?.training_reminder_sent;
    if (Array.isArray(trainingReminders) && trainingReminders.length > 0) {
      trainingReminders.forEach((email: any) => {
        emailHistory.push({
          type: 'Pre-Event Info (training)',
          sent_at: email.sent_at,
          sent_to: Array.isArray(email.sent_to) ? email.sent_to : [email.sent_to]
        });
      });
    }
    
    // Kit Delivery - check both formatted_custom_data and custom_data
    let kitDeliveryEmails: any[] = [];
    
    // First check formatted_custom_data for kit_delivery_email (array format)
    if (thisEvent?.formatted_custom_data?.kit_delivery_email) {
      kitDeliveryEmails = Array.isArray(thisEvent.formatted_custom_data.kit_delivery_email) 
        ? thisEvent.formatted_custom_data.kit_delivery_email 
        : [];
    }
    
    // If not found, check custom_data (parsed JSON)
    if (kitDeliveryEmails.length === 0 && thisEvent?.custom_data) {
      try {
        const customData = JSON.parse(thisEvent.custom_data);
        if (customData.kit_delivery_email && Array.isArray(customData.kit_delivery_email)) {
          kitDeliveryEmails = customData.kit_delivery_email;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // If still not found, check for legacy kit_delivery_email_sent (single timestamp)
    if (kitDeliveryEmails.length === 0) {
      const kitEmailSent = thisEvent?.formatted_custom_data?.kit_delivery_email_sent;
      if (kitEmailSent) {
        // Convert single timestamp to array format
        kitDeliveryEmails = [{ sent_at: kitEmailSent, sent_to: [] }];
      }
    }
    
    // Add kit delivery emails to history
    if (kitDeliveryEmails.length > 0) {
      kitDeliveryEmails.forEach((email: any) => {
        if (email && email.sent_at) {
          emailHistory.push({
            type: 'Kit Tracking Info',
            sent_at: email.sent_at,
            sent_to: Array.isArray(email.sent_to) ? email.sent_to : (email.sent_to ? [email.sent_to] : [])
          });
        }
      });
    }
    
    // Event Approved (event_approved_resend)
    const eventApprovedEmails = thisEvent?.formatted_custom_data?.event_approved_resend;
    if (Array.isArray(eventApprovedEmails) && eventApprovedEmails.length > 0) {
      eventApprovedEmails.forEach((email: any) => {
        emailHistory.push({
          type: 'Event Approved',
          sent_at: email.sent_at,
          sent_to: Array.isArray(email.sent_to) ? email.sent_to : [email.sent_to]
        });
      });
    }
    
    // Sort by date (most recent first)
    emailHistory.sort((a, b) => {
      const dateA = new Date(a.sent_at).getTime();
      const dateB = new Date(b.sent_at).getTime();
      return dateB - dateA;
    });
    
    return emailHistory;
  };

  const generateLastSentText = (emailType: string) => {
    const getLastSentAt = (emailsSent: Array<emailsSent>) => {
      if (!emailsSent) return 'Not Yet Sent';
      const parseDate = (email: emailsSent) => new Date(email.sent_at);
      emailsSent.sort((a, b) => parseDate(b).getTime() - parseDate(a).getTime());
      const mostRecentEmail = emailsSent[0];
      return `${moment(mostRecentEmail.sent_at).tz('America/New_York').format('MMMM Do, h:mm a')} (EST) to ${mostRecentEmail.sent_to.length > 1 ? mostRecentEmail.sent_to.join(', ') : mostRecentEmail.sent_to}`;
    }
    if (!emailType) return;
    if (emailType.id === 'pre_event') {
      return (
        <p className={"send-emails-subtext"}>This email was last sent at: {getLastSentAt(thisEvent?.formatted_custom_data?.training_reminder_sent)}</p>
      );
    } else if (emailType.id === 'kit_delivery') {
      return (
        <p className={"send-emails-subtext"}>This email was last sent at: {moment(thisEvent?.formatted_custom_data?.kit_delivery_email_sent).tz('America/New_York').format('MMMM Do, h:mm a') || 'Not Yet Sent'}</p>
      );
    } else if (emailType.id === 'event_approved') {
      return (
        <p className={"send-emails-subtext"}>This email was last sent at: {getLastSentAt(thisEvent?.formatted_custom_data?.event_approved_resend)}</p>
      );
    }
  }

  const rejectEligibility = () => {
    d4ur.setIneligibleEventRequest(thisEvent!.event_request_id!).then((res) => {
      setDialogDisplayed("none");
      history.push('/events');
    });
  };

  const filterTimeZones = (
    filter: FilterDescriptor | CompositeFilterDescriptor
  ) => {
    return filterBy(raw_timezones, filter);
  };

  const filterTZ = (event: DropDownListFilterChangeEvent) => {
    setTimeZones(filterTimeZones(event.filter));
  };

  const formatRange = (start?: Date, end?: Date) => {
    let range = "";
    if (start) {
      range += moment(start).format("h:mm a");
    }
    if (start && end) {
      range += " - ";
    }
    if (end) {
      range += moment(end).format("h:mm a");
    }

    return range;
  };

  const { file_id } = JSON.parse(
    thisEvent?.event_custom_data || "{}"
  );

  const onSubmitPlan = React.useCallback(
    async (dataItem) => {
      console.log("dataItem!!!!!!!!", dataItem);
      setFormDisplayed("none");

      // format dates properly
      if (dataItem.start_date) {
        dataItem.start_date = moment(dataItem.start_date).format("YYYY-MM-DD");
      }
      if (dataItem.start_time) {
        dataItem.start_time = moment(dataItem.start_time).format("HH:mm:00");
      }
      if (dataItem.end_time) {
        dataItem.end_time = moment(dataItem.end_time).format("HH:mm:00");
      }

      let customData = JSON.parse(thisEvent!.custom_data || "{}");
      customData = {
        ...customData,
        requested_month: dataItem.requested_month,
        requested_date: dataItem.requested_date,
      };

      dataItem.custom_data = JSON.stringify(customData);

      let eventCustomData = JSON.parse(thisEvent!.event_custom_data || "{}");
      eventCustomData = {
        ...eventCustomData,
        centuryParty: dataItem.event_type,
      };

      dataItem.event_custom_data = JSON.stringify(eventCustomData);

      const [selectedFile] = files;
      const rawFile =
        selectedFile && selectedFile.getRawFile && selectedFile.getRawFile();
      // if (rawFile) {
      //     dataItem.event_plan_file = rawFile;
      // }

      const call: any = await d4ur.saveEventPlan(
        new D4URxEvent(dataItem),
        rawFile
      );
      if (call.result.errors) {
        console.log("[EventDetailScreen]", "Update Event Request Error");
        setFormError(call.result.errors.join("\n"));
      } else {
        console.log("[EventDetailScreen]", "Event Request updated");
        history.push('/events');
      }
    },
    [d4ur, files, history, thisEvent]
  );

  const onDenyPlan = React.useCallback(
    async (dataItem) => {
      console.log("dataItem!!!!!!!!", dataItem);
      d4ur
        .denyPlan(thisEvent!.event_request_id!, dataItem.deny_message)
        .then((res) => {
          setDialogDisplayed("none");
          history.push("/events");
        });
    },
    [d4ur, history, thisEvent]
  );

  const onSubmitFinalApproval = React.useCallback(
    async (dataItem) => {
      const call: any = await d4ur.submitFinalApproval(
        thisEvent?.event_request_id
      );
      if (call.result.errors) {
        console.log("[EventDetailScreen]", "Event Approved Error");
        setFormError(call.result.errors.join("\n"));
      } else {
        console.log("[EventDetailScreen]", "Event Approved");
        history.push("/events");
      }
    },
    [d4ur, history, thisEvent]
  );

  const onSubmitReimbursementApproval = React.useCallback(
    async (dataItem) => {
      console.log("DATA ITEM SUBMIT", dataItem);
      if (!dataItem.reimbursement_amount || dataItem.reimbursement_amount <= 0) {
        setFormError("Reimbursement amount is required.");
        return;
      }
      setFormError("");
      const call: any = await d4ur.submitReimbursementApproval(
        thisEvent?.event_id,
        dataItem.reimbursement_amount,
      );
      if (call.result.errors) {
        console.log("[EventDetailScreen]", "Submit Reimbursement Error");
        setFormError(call.result.errors.join("\n"));
      }
      if (
        Object.keys(call.result).includes("verified") &&
        call.result.verified === false
      ) {
        console.log("success but not verified!!");
        toggleAddressVerified(false);
        // display message and display button to force through
      } else {
        toggleAddressVerified(true);
        console.log("[EventDetailScreen]", "Reimbursement Approved");
        history.push("/events");
      }
    },
    [d4ur, history, thisEvent]
  );

  const onSubmitReimbursementApprovalForceAddress = React.useCallback(
    async (dataItem) => {
      const call: any = await d4ur.submitForcedReimbursementApproval(
        thisEvent?.event_id,
        dataItem.reimbursement_amount,
      );
      console.log("DONATION FORCED CALL", call);

      if (call.result.errors) {
        console.log("[EventDetailScreen]", "Reimbursement Approved Error");
        setFormError(call.result.errors.join("\n"));
      } else {
        toggleAddressVerified(true);
        console.log("[EventDetailScreen]", "Reimbursement Approved");
        history.push("/events");
      }
    },
    [d4ur, history, thisEvent]
  );

  const onSubmitDonationApproval = React.useCallback(
    async (dataItem) => {

      const call: any = await d4ur.submitDonationApproval(
        thisEvent?.event_id,
        dataItem.event_summary,
        dataItem.check_amount,
        dataItem.valid_post_count,
        dataItem.bonus_valid_count,
        normalizeMemo(dataItem.memo) ?? '',
      );

      if (call.result.errors) {
        console.log("[EventDetailScreen]", "Donation Approved Error");
        setFormError(call.result.errors.join("\n"));
      }
      if (
        Object.keys(call.result).includes("verified") &&
        call.result.verified === false
      ) {
        console.log("success but not verified!!");
        toggleAddressVerified(false);
        // display message and display button to force through
      } else {
        toggleAddressVerified(true);
        console.log("[EventDetailScreen]", "Donation Approved");
        history.push("/events");
      }
    },
    [d4ur, history, thisEvent]
  );

  const onSubmitDonationApprovalForceAddress = React.useCallback(
    async (dataItem) => {
      console.log('dataItem', dataItem);
      const call: any = await d4ur.submitForcedDonationApproval(
        thisEvent?.event_id,
        dataItem.event_summary,
        dataItem.check_amount,
        dataItem.valid_post_count,
        dataItem.bonus_valid_count,
        normalizeMemo(dataItem.memo) ?? '',
      );
      console.log("DONATION FORCED CALL", call);

      if (call.result.errors) {
        console.log("[EventDetailScreen]", "Donation Approved Error");
        setFormError(call.result.errors.join("\n"));
      } else {
        toggleAddressVerified(true);
        console.log("[EventDetailScreen]", "Donation Approved");
        history.push("/events");
      }
    },
    [d4ur, history, thisEvent]
  );

  const eventTypes = [
    { text: "No test-drive or walkaround", value: "basic" },
    { text: "Test-Drive", value: "test_drive" },
    { text: "Walkaround", value: "walkaround" },
  ];

  const surveyLink = `https://${siteDomain}/${thisEvent?.pre_reg_hash}`;

  const generateEventAddress = () => {
    console.log("thisEvent!!!!!!", thisEvent);
    const location = thisEvent?.event_custom_data ? JSON.parse(thisEvent?.event_custom_data).event_location_type : '';
    if (location === "The School" || location === "The Organization") {
      return `${titleCase(charity?.address_1)} ${titleCase(charity?.address_2)} ${titleCase(charity?.city)}, ${charity?.state} ${charity?.postal_code}`;
    } else if (location === "The Dealership") {
      return `${titleCase(dealer?.address)} ${titleCase(dealer?.address2)} ${titleCase(dealer?.city)}, ${dealer?.state} ${dealer?.zip}`;
    } else {
      return `${titleCase(thisEvent?.address)} ${titleCase(thisEvent?.city)}, ${thisEvent?.state} ${thisEvent?.zip_code}`;
    }
  }

  const generateDealerNotes = (dealer_notes: any) => {
    if (!dealer_notes) {
      return 'No Notes'
    }
    
    let parsedNotes = [];
    try {
      if (typeof dealer_notes === 'string') {
        // Clean up the string and check if it's valid JSON
        const cleanedNotes = dealer_notes.trim();
        if (cleanedNotes === '' || cleanedNotes === 'null' || cleanedNotes === 'undefined' || cleanedNotes === '[]') {
          return 'No Notes';
        } else if (cleanedNotes.startsWith('[') || cleanedNotes.startsWith('{')) {
          parsedNotes = JSON.parse(cleanedNotes);
        } else {
          console.warn('dealer_notes not in JSON format:', cleanedNotes);
          return 'No Notes';
        }
      } else if (Array.isArray(dealer_notes)) {
        parsedNotes = dealer_notes;
      } else {
        return 'No Notes';
      }
    } catch (e) {
      console.error('Failed to parse dealer_notes:', e);
      return 'No Notes';
    }

    if (!Array.isArray(parsedNotes) || parsedNotes.length === 0) {
      return 'No Notes';
    }

    const formattedNotes = parsedNotes
      .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
      .map((note: any) => {
        const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
        return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
      });

    return (
      <td>
        {formattedNotes.map((note: string, index: number) => (
          <div key={index} style={{ borderBottom: "1px solid lightgrey" }}>{note}</div>
        ))}
      </td>
    );
  }

  const [outreachNotesModalVisible, setOutreachNotesModalVisible] = useState<boolean>(false);
  const [addOutreachNoteModalVisible, setAddOutreachNoteModalVisible] = useState<boolean>(false);
  const [showAddNoteInModal, setShowAddNoteInModal] = useState<boolean>(false);
  const [newOutreachNoteText, setNewOutreachNoteText] = useState<string>('');
  const [newOutreachNoteTextInModal, setNewOutreachNoteTextInModal] = useState<string>('');
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null);
  const [editedNoteText, setEditedNoteText] = useState<string>('');
  const [selectedEventNotes, setSelectedEventNotes] = useState<OutreachNote[]>([]);
  const [originalNoteContent, setOriginalNoteContent] = useState<string>('');

  const handleSaveOutreachNote = () => {
    if (!newOutreachNoteText.trim()) {
      setFormError('Note text is required.');
      return;
    }

    if (thisEvent?.event_id && login.user?.credentials) {
      d4ur.addOutreachNote(
        thisEvent.event_id,
        newOutreachNoteText.trim(),
        login.user.credentials.email,
        login.user.credentials.display_name
      ).then((ret) => {
        if (ret.response.status === 200) {
          setSuccessMessage('Note added successfully!');
          setAddOutreachNoteModalVisible(false);
          setNewOutreachNoteText('');
          
          // Refresh the full data to update the UI immediately
          getRequestData();
          
          setTimeout(() => {
            setSuccessMessage('');
          }, 2500);
        } else {
          setErrorMessage(ret.result.message || 'Error adding note.');
        }
      })
      .catch(e => {
        console.log('ADD OUTREACH NOTE ERROR', e);
        setErrorMessage('Error adding note.');
      });
    }
  };

  const handleSaveOutreachNoteInModal = () => {
    if (!newOutreachNoteTextInModal.trim()) {
      setFormError('Note text is required.');
      return;
    }

    if (thisEvent?.event_id && login.user?.credentials) {
      d4ur.addOutreachNote(
        thisEvent.event_id,
        newOutreachNoteTextInModal.trim(),
        login.user.credentials.email,
        login.user.credentials.display_name
      ).then((ret) => {
        if (ret.response.status === 200) {
          setSuccessMessage('Note added successfully!');
          setNewOutreachNoteTextInModal('');
          setShowAddNoteInModal(false);
          
          // Create the new note object to add to local state
          const newNote = {
            note: newOutreachNoteTextInModal.trim(),
            created_at: new Date().toLocaleString(), // Approximate timestamp
            created_by: login.user.credentials.email
          };
          
          // Update local modal state immediately
          setSelectedEventNotes(prevNotes => [...prevNotes, newNote]);
          
          // Also refresh the full data in background
          getRequestData();
          
          setTimeout(() => {
            setSuccessMessage('');
          }, 2500);
        } else {
          setErrorMessage(ret.result.message || 'Error adding note.');
        }
      })
      .catch(e => {
        console.log('ADD OUTREACH NOTE ERROR', e);
        setErrorMessage('Error adding note.');
      });
    }
  };

  const handleSaveEditedOutreachNote = (originalTimestamp: string) => {
    if (!editedNoteText.trim()) {
      return;
    }

    if (thisEvent?.event_id && login.user?.credentials) {
      d4ur.updateOutreachNote(
        thisEvent.event_id,
        originalTimestamp,
        originalNoteContent, // Pass the original note content
        editedNoteText.trim()
      ).then((ret) => {
        if (ret.response.status === 200) {
          setEditingNoteIndex(null);
          setEditedNoteText('');
          setOriginalNoteContent('');
          
          // Update local modal state immediately
          setSelectedEventNotes(prevNotes => 
            prevNotes.map((note, index) => 
              index === editingNoteIndex 
                ? { ...note, note: editedNoteText.trim() }
                : note
            )
          );
          
          // Also refresh the full data in background
          getRequestData();
        }
      })
      .catch(e => {
        console.log('UPDATE OUTREACH NOTE ERROR', e);
      });
    }
  };

  const handleToggleOutreachStatus = () => {
    if (thisEvent?.event_id) {
      d4ur.setOutreachStatus(thisEvent.event_id, thisEvent.outreach_done === null).then((ret) => {
        if (ret.response.status === 200) {
          setSuccessMessage('Successfully updated outreach status.');
          getRequestData(); // Refresh the data
          setTimeout(() => {
            setSuccessMessage('');
          }, 2500);
        } else {
          setErrorMessage(ret.result.message || 'Error updating outreach status.');
        }
      })
      .catch(e => console.log('UPDATE OUTREACH ERROR', e));
    }
  };

  const openOutreachNotesModal = () => {
    let notes = [];
    if (thisEvent?.outreach_notes) {
      if (typeof thisEvent.outreach_notes === 'string') {
        try {
          notes = JSON.parse(thisEvent.outreach_notes);
        } catch (e) {
          console.error('Failed to parse outreach_notes:', e);
        }
      } else if (Array.isArray(thisEvent.outreach_notes)) {
        notes = thisEvent.outreach_notes;
      }
    }
    setSelectedEventNotes(notes);
    setShowAddNoteInModal(false);
    setNewOutreachNoteTextInModal('');
    setOutreachNotesModalVisible(true);
  };

  const generateOutreachNotes = (outreach_notes: any, outreach_done: any) => {
    console.log('outreach_notes in Event.tsx:', outreach_notes, typeof outreach_notes);
    
    let notes = [];
    
    // Handle different formats the data might be in
    if (typeof outreach_notes === 'string') {
      try {
        notes = JSON.parse(outreach_notes);
      } catch (e) {
        console.error('Failed to parse outreach_notes:', e);
      }
    } else if (Array.isArray(outreach_notes)) {
      notes = outreach_notes;
    }
    
    const notesCount = notes ? notes.length : 0;
    
    return (
      <div style={{ marginTop: "5px" }}>
        {/* Outreach Done Row */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
          <span style={fontSemiBold}>Outreach Done: </span>
          <input
            type="checkbox"
            checked={outreach_done !== null}
            style={{ width: "16px", height: "16px", cursor: "pointer", marginLeft: "8px" }}
            onChange={handleToggleOutreachStatus}
          />
        </div>
        
        {/* Outreach Notes Section */}
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
            <span style={fontSemiBold}>Outreach Notes: </span>
            
            {notesCount > 0 ? (
              <span
                className={'k-icon k-i-edit'}
                style={{ color: '#666', marginLeft: '8px', fontSize: '12px', cursor: 'pointer' }}
                onClick={openOutreachNotesModal}
              />
            ) : (
              <Button
                className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                style={{
                  backgroundColor: "#007bff",
                  borderColor: "#007bff",
                  color: "white",
                  marginLeft: "8px"
                }}
                onClick={() => setAddOutreachNoteModalVisible(true)}
              >
                Add Note
              </Button>
            )}
          </div>
          
          {/* Display Notes */}
          <div style={{ marginLeft: "0px" }}>
            {notesCount > 0 ? (
              notes
                .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
                .map((note: any, index: number) => {
                  const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                  return (
                    <div key={index} style={{ 
                      borderBottom: "1px solid lightgrey", 
                      paddingBottom: "4px", 
                      marginBottom: "4px" 
                    }}>
                      {formattedDateTime} - {note.created_by}: {note.note}
                    </div>
                  );
                })
            ) : (
              <div style={{ fontStyle: "italic", color: "#666" }}>No Notes</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const [customEmailChecked, setCustomEmailChecked] = useState<boolean>(false);
  const [cancelEmailBody, setCancelEmailBody] = useState<string>('We regret to inform you that the Drive For Your Community event you registered for has been cancelled. We apologize for any inconvenience this may cause. If you have any questions, please contact us.');

  const [cancellationReason, setCancellationReason] = useState<string>('');
  const [cancellationNotes, setCancellationNotes] = useState<string>('');
  const selectedCancellationReason = CANCELLATION_REASONS.find((r) => r.value === cancellationReason);
  const cancellationValid = !!selectedCancellationReason
    && (selectedCancellationReason.notes !== 'required' || cancellationNotes.trim() !== '');

  const handleCancelEvent = () => {
    if (!cancellationValid) return;
    const notes = selectedCancellationReason?.notes === 'none' ? undefined : cancellationNotes.trim() || undefined;
    d4ur.cancelEvent(thisEvent!, customEmailChecked ? cancelEmailBody : undefined, cancellationReason, notes)
      .then((res: any) => {
        setDialogDisplayed("none");
        history.push('/events');
      })
      .catch((err: any) => {
        setErrorMessage('Failed to cancel event: ' + err.message);
        setDialogDisplayed("none");
      });
  }

  const handleReschedule = (dataItem: any) => {
    return d4ur.rescheduleEvent(thisEvent!, dataItem.rescheduleDate)
        .then((res: any) => {
          setDialogDisplayed("none");
          getRequestData();
        })
        .catch((err: any) => {
          setErrorMessage('Failed to reschedule event: ' + err.message);
          setDialogDisplayed("none");
        });
  }

  // Add this useEffect near the top of the EventDetailScreen component (after the other useEffect)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .k-dialog .k-window-titlebar {
        background-color: #007bff !important;
        border-color: #007bff !important;
      }
      .k-dialog .k-window-title {
        color: white !important;
      }
      .k-dialog .k-window-titlebar .k-window-action {
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add this function after the other outreach functions
  const handleDeleteOutreachNote = (originalTimestamp: string, noteContent: string, noteIndex: number) => {
    if (thisEvent?.event_id) {
      if (window.confirm('Are you sure you want to delete this note?')) {
        d4ur.deleteOutreachNote(
          thisEvent.event_id,
          originalTimestamp,
          noteContent
        ).then((ret) => {
          if (ret.response.status === 200) {
            // Update local modal state immediately by removing the note
            setSelectedEventNotes(prevNotes => 
              prevNotes.filter((_, index) => index !== noteIndex)
            );
            
            // Also refresh the full data in background
            getRequestData();
          }
        })
        .catch(e => {
          console.log('DELETE OUTREACH NOTE ERROR', e);
        });
      }
    }
  };

  const handleSaveMemo = async () => {
    if (!thisEvent?.event_id) return;
    setIsLoading(true);
    try {
      const normalizedMemo = normalizeMemo(memoText);
      await d4ur.updatePreApprovalMemo(
        thisEvent.event_id,
        normalizedMemo,
        checkPayableText.trim() || null,
      );
      setPreApprovalMemo(normalizedMemo ?? '');
      setPreApprovalCheckPayable(checkPayableText.trim());
      setSuccessMessage('Banking info updated successfully.');
      setMemoDialogVisible(false);
    } catch {
      setErrorMessage('Failed to update banking info.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler functions for dealer notes
  const handleSaveDealerNote = async () => {
    if (!newDealerNoteText.trim() || !dealer?.dealer_id) {
      return;
    }
    
    try {
      const ret = await d4ur.addDealerNoteByDealerId(
        dealer.dealer_id,
        newDealerNoteText.trim(),
        login.user?.credentials?.email || '',
        login.user?.credentials?.display_name || ''
      );
      
      if ((ret as any).response?.status === 200) {
        setNewDealerNoteText('');
        
        // Update dealer state immediately for instant UI update
        if (dealer) {
          const newNote = {
            note: newDealerNoteText.trim(),
            created_at: moment().format("MMM DD YYYY hh:mm A"),
            created_by: login.user?.credentials?.email || '',
            created_by_name: login.user?.credentials?.display_name || ''
          };
          
          let updatedNotes: any[] = [];
          const currentNotes = dealer.dealer_notes;
          
          if (typeof currentNotes === 'string') {
            try {
              updatedNotes = JSON.parse(currentNotes);
            } catch {
              updatedNotes = [];
            }
          } else if (Array.isArray(currentNotes)) {
            updatedNotes = [...currentNotes];
          }
          
          updatedNotes.push(newNote);
          
          // Update local state immediately for instant display
          updateLocalNotes(updatedNotes);
          
          setDealer(prevDealer => ({
            ...prevDealer!,
            dealer_notes: updatedNotes
          }));
        }
        
        await getRequestData(); // Reload data to get updated notes from server
        setSuccessMessage('Dealer note added successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        console.log('DEBUG: API call failed with status:', (ret as any).response?.status);
        setErrorMessage('Failed to add dealer note. Please try again.');
      }
    } catch (error) {
      console.error('DEBUG: Error adding dealer note:', error);
      setErrorMessage('Failed to add dealer note. Please try again.');
    }
  };

  const handleSaveDealerNoteInModal = async () => {
    // Try to get dealer ID from multiple sources
    let dealerId = selectedDealerForNoteId || dealerNotesModalDealerId || dealer?.dealer_id || thisEvent?.dealer_id || null;
    
    if (!newDealerNoteTextInModal.trim() || !dealerId) {
      return;
    }
    
    try {
      const ret = await d4ur.addDealerNoteByDealerId(
        dealerId,
        newDealerNoteTextInModal.trim(),
        login.user?.credentials?.email || '',
        login.user?.credentials?.display_name || ''
      );
      
      if ((ret as any).response?.status === 200) {
        setNewDealerNoteTextInModal('');
        setShowAddDealerNoteInModal(false);
        setAddDealerNoteModalVisible(false);
        
        // Update dealer state immediately for instant UI update
        if (dealer) {
          const newNote = {
            note: newDealerNoteTextInModal.trim(),
            created_at: moment().format("MMM DD YYYY hh:mm A"),
            created_by: login.user?.credentials?.email || '',
            created_by_name: login.user?.credentials?.display_name || ''
          };
          
          let updatedNotes: any[] = [];
          const currentNotes = dealer.dealer_notes;
          
          if (typeof currentNotes === 'string') {
            try {
              updatedNotes = JSON.parse(currentNotes);
            } catch {
              updatedNotes = [];
            }
          } else if (Array.isArray(currentNotes)) {
            updatedNotes = [...currentNotes];
          }
          
          updatedNotes.push(newNote);
          
          // Update local state immediately for instant display
          updateLocalNotes(updatedNotes);
          
          setDealer(prevDealer => ({
            ...prevDealer!,
            dealer_notes: updatedNotes
          }));
        }
        
        await getRequestData(); // Reload data to get updated notes from server
        setSuccessMessage('Dealer note added successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        console.log('DEBUG: API call failed with status:', (ret as any).response?.status);
        setErrorMessage('Failed to add dealer note. Please try again.');
      }
    } catch (error) {
      console.error('DEBUG: Error adding dealer note:', error);
      setErrorMessage('Failed to add dealer note. Please try again.');
    }
  };

  const handleSaveEditedDealerNote = async (originalTimestamp: string) => {
    console.log('DEBUG: handleSaveEditedDealerNote called');
    console.log('DEBUG: editedDealerNoteText:', editedDealerNoteText);
    console.log('DEBUG: editingDealerNoteIndex:', editingDealerNoteIndex);
    console.log('DEBUG: selectedDealerForNoteId:', selectedDealerForNoteId);
    console.log('DEBUG: dealerNotesModalDealerId:', dealerNotesModalDealerId);
    
    // Try to get dealer ID from multiple sources
    let dealerId = selectedDealerForNoteId || dealerNotesModalDealerId || dealer?.dealer_id || thisEvent?.dealer_id || null;
    console.log('DEBUG: Found dealer ID from multiple sources:', {
      fromSelectedDealerForNoteId: selectedDealerForNoteId,
      fromDealerNotesModalDealerId: dealerNotesModalDealerId,
      fromDealer: dealer?.dealer_id,
      fromEvent: thisEvent?.dealer_id,
      finalDealerId: dealerId
    });
    
    if (!editedDealerNoteText.trim() || editingDealerNoteIndex === null || !dealerId) {
      console.log('DEBUG: Early return - missing text, index, or dealer_id');
      return;
    }
    
    try {
      console.log('DEBUG: Calling updateDealerNoteByDealerId with:', {
        dealer_id: dealerId,
        originalTimestamp,
        originalContent: originalDealerNoteContent,
        newContent: editedDealerNoteText.trim()
      });
      
      const ret = await d4ur.updateDealerNoteByDealerId(
        dealerId,
        originalTimestamp,
        originalDealerNoteContent,
        editedDealerNoteText.trim()
      );
      
      console.log('DEBUG: API response:', ret);
      
      if ((ret as any).response?.status === 200) {
        // Update dealer state immediately for instant UI update
        if (dealer && editingDealerNoteIndex !== null) {
          let updatedNotes: any[] = [];
          const currentNotes = dealer.dealer_notes;
          
          if (typeof currentNotes === 'string') {
            try {
              updatedNotes = JSON.parse(currentNotes);
            } catch {
              updatedNotes = [];
            }
          } else if (Array.isArray(currentNotes)) {
            updatedNotes = [...currentNotes];
          }
          
          // Update the specific note
          if (updatedNotes[editingDealerNoteIndex]) {
            updatedNotes[editingDealerNoteIndex] = {
              ...updatedNotes[editingDealerNoteIndex],
              note: editedDealerNoteText.trim()
            };
          }
          
          // Update local state immediately for instant display
          updateLocalNotes(updatedNotes);
          
          setDealer(prevDealer => ({
            ...prevDealer!,
            dealer_notes: updatedNotes
          }));
          
          // Update modal state as well
          setSelectedDealerNotes(updatedNotes);
        }
        
        setEditingDealerNoteIndex(null);
        setEditedDealerNoteText('');
        setOriginalDealerNoteContent('');
        await getRequestData(); // Reload data to get updated notes from server
        
        setSuccessMessage('Dealer note updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        console.log('DEBUG: API call failed with status:', (ret as any).response?.status);
        setErrorMessage('Failed to update dealer note. Please try again.');
      }
    } catch (error) {
      console.error('DEBUG: Error updating dealer note:', error);
      setErrorMessage('Failed to update dealer note. Please try again.');
    }
  };

  const handleDeleteDealerNote = async (originalTimestamp: string, noteContent: string, noteIndex: number) => {
    console.log('DEBUG: handleDeleteDealerNote called');
    console.log('DEBUG: originalTimestamp:', originalTimestamp);
    console.log('DEBUG: noteContent:', noteContent);
    console.log('DEBUG: noteIndex:', noteIndex);
    console.log('DEBUG: selectedDealerForNoteId:', selectedDealerForNoteId);
    console.log('DEBUG: dealerNotesModalDealerId:', dealerNotesModalDealerId);
    
    // Try to get dealer ID from multiple sources
    let dealerId = selectedDealerForNoteId || dealerNotesModalDealerId || dealer?.dealer_id || thisEvent?.dealer_id || null;
    console.log('DEBUG: Found dealer ID from multiple sources:', {
      fromSelectedDealerForNoteId: selectedDealerForNoteId,
      fromDealerNotesModalDealerId: dealerNotesModalDealerId,
      fromDealer: dealer?.dealer_id,
      fromEvent: thisEvent?.dealer_id,
      finalDealerId: dealerId
    });
    
    if (!dealerId) {
      console.log('DEBUG: Early return - missing dealer_id');
      return;
    }
    
    try {
      console.log('DEBUG: Calling deleteDealerNoteByDealerId with:', {
        dealer_id: dealerId,
        originalTimestamp,
        noteContent
      });
      
      const ret = await d4ur.deleteDealerNoteByDealerId(
        dealerId,
        originalTimestamp,
        noteContent
      );
      
      console.log('DEBUG: API response:', ret);
      
      if ((ret as any).response?.status === 200) {
        // Update local state immediately for instant display
        const updatedNotes = localDealerNotes.filter((_, index) => index !== noteIndex);
        updateLocalNotes(updatedNotes);
        
        // Update modal state as well
        setSelectedDealerNotes(updatedNotes);
        
        await getRequestData(); // Reload data to get updated notes from server
        
        setSuccessMessage('Dealer note deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        console.log('DEBUG: API call failed with status:', (ret as any).response?.status);
        setErrorMessage('Failed to delete dealer note. Please try again.');
      }
    } catch (error) {
      console.error('DEBUG: Error deleting dealer note:', error);
      setErrorMessage('Failed to delete dealer note. Please try again.');
    }
  };

  // Add useEffect to handle body scroll locking
  useEffect(() => {
    if (outreachNotesModalVisible || addOutreachNoteModalVisible || dealerNotesModalVisible || addDealerNoteModalVisible) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll on component unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [outreachNotesModalVisible, addOutreachNoteModalVisible, dealerNotesModalVisible, addDealerNoteModalVisible]);



  return (
    <div id="Eligibility" className="eligibility-page">
      {shareChangesVisible && (
        <Dialog title={"Please confirm"} onClose={() => setShareChangesVisible(false)}>
          <p style={{ margin: "25px", textAlign: "center" }}>
            Do you want to notify the dealer ({dealer?.primary_contact} at {dealer?.dealer_name}) that changes were made to their event?
          </p>
          <DialogActionsBar>
            <Button
              className="ford-blue-btn"
              onClick={() => {
                setFormDisplayed("none");
                history.push('/events');
              }}
            >
              No
            </Button>
            <Button
              className="ford-blue-btn"
              onClick={() => {
                window.location.href = `mailto:${dealer?.contact_email}?subject=Notification%20of%20Changes%20to%20Your%20D4URC%20Event`
                setFormDisplayed("none");
                history.push('/events');
              }}
            >
              Yes, Open My Email
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
      {dialogDisplayed === "approveEligibility" && (
        <Dialog>
          <h3>Assign a Planner</h3>
          <Form
            onSubmit={onSubmitPlanner}
            initialValues={{
              ...formValues,
            }}
            render={(formRenderProps) => (
              <FormElement horizontal={true} style={{ width: 250 }}>
                {formError && <Error>{formError}</Error>}
                <Field
                  component={DropDownList}
                  id={"assigned_planner"}
                  name={"assigned_planner"}
                  label={localizationService.toLanguageString(
                    "custom.assigned_planner",
                    "assigned_planner"
                  )}
                  data={planners}
                  dataItemKey="user_id"
                  textField="display_name"
                  required={true}
                />
                <br />
                <DialogActionsBar>
                  <Button
                    className="ford-blue-btn"
                    primary={true}
                    type={"submit"}
                    disabled={!formRenderProps.allowSubmit}
                  >
                    {localizationService.toLanguageString(
                      "custom.save",
                      "save"
                    )}
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}
      {dialogDisplayed === "denyPlan" && (
        <Dialog>
          <h3>Deny Plan</h3>
          <Form
            onSubmit={onDenyPlan}
            initialValues={{
              ...formValues,
            }}
            render={(formRenderProps) => (
              <FormElement horizontal={true}>
                <p>Please enter a message for the Dealer.</p>
                {formError && <Error>{formError}</Error>}
                <Field
                  component={TextArea}
                  id={"deny_message"}
                  name={"deny_message"}
                  label={localizationService.toLanguageString(
                    "custom.deny_plan_reason",
                    "deny_plan_reason"
                  )}
                  required={true}
                />
                <DialogActionsBar>
                  <Button
                    className="ford-blue-btn"
                    primary={true}
                    type={"submit"}
                    disabled={!formRenderProps.allowSubmit}
                  >
                    {localizationService.toLanguageString(
                      "custom.deny_plan",
                      "deny_plan"
                    )}
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}
      {dialogDisplayed === "cancel_confirm" && (
        <Dialog>
          <h3>Cancel Event?</h3>
          <p>Are you sure you want to cancel event: <span style={{ fontWeight: 'bold' }}>{thisEvent?.event_name}</span> scheduled for <span style={{ fontWeight: 'bold' }}>{moment(thisEvent?.start_date).format('MM/DD/YYYY')}</span>?</p>
          <p>This action will notify RRD of the cancellation.</p>

          <div style={{ margin: "15px 0" }}>
            <p style={{ fontWeight: 700, marginBottom: 8 }}>Reason for cancellation:</p>
            {CANCELLATION_REASONS.map((reason) => (
              <label key={reason.value} style={{ display: "flex", alignItems: "center", marginBottom: "8px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="cancellation_reason"
                  style={{ marginRight: "10px", width: "18px", height: "18px" }}
                  checked={cancellationReason === reason.value}
                  onChange={() => setCancellationReason(reason.value)}
                />
                <span>{reason.label}</span>
              </label>
            ))}
            {selectedCancellationReason && selectedCancellationReason.notes !== 'none' && (
              <textarea
                style={{
                  width: "97%",
                  minHeight: "60px",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontFamily: "inherit",
                  fontSize: "14px"
                }}
                maxLength={1000}
                placeholder={selectedCancellationReason.notes === 'required'
                  ? 'Describe the reason for cancellation (required)'
                  : 'Additional info (optional)'}
                value={cancellationNotes}
                onChange={(e) => setCancellationNotes(e.target.value)}
              />
            )}
          </div>

          {registrants?.completed_registrations > 0 && (
            <div style={{ margin: "15px 0" }}>
              <label style={{ display: "flex", alignItems: "center", marginBottom: "10px", cursor: "pointer" }}>
                <input 
                  type="checkbox" 
                  style={{ marginRight: "10px", width: "18px", height: "18px" }} 
                  checked={customEmailChecked} 
                  onChange={() => setCustomEmailChecked(!customEmailChecked)} 
                />
                <span>Send Email to Pre-Registrations ({registrants?.completed_registrations} registrants)</span>
              </label>
              
              {customEmailChecked && (
                <textarea 
                  style={{ 
                    width: "97%", 
                    minHeight: "100px", 
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                    fontSize: "14px"
                  }} 
                  placeholder="Enter custom email body text..."
                  value={cancelEmailBody}
                  onChange={(e) => setCancelEmailBody(e.target.value)}
                />
              )}
            </div>
          )}
          
          <DialogActionsBar>
            <Button
              className="ford-blue-btn" 
              onClick={() => {
                setCancellationReason('');
                setCancellationNotes('');
                setDialogDisplayed("none");
              }}>
              No, Go Back
            </Button>
            &nbsp;
            <Button
              className="ford-red-btn"
              onClick={() => handleCancelEvent()}
              disabled={!cancellationValid}
              >
              Yes, Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
      {dialogDisplayed === "approvePlan" && (
        <Dialog>
          <h3>Approve Event</h3>
          <Form
            onSubmit={onSubmitFinalApproval}
            ignoreModified={true}
            initialValues={{
              ...formValues,
            }}
            render={(formRenderProps) => (
              <FormElement horizontal={true}>
                <p>
                  Are you sure you want to approve this event? The dealer will
                  be notified immediately.
                </p>
                {formError && <Error>{formError}</Error>}
                <DialogActionsBar>
                  <Button
                    className="ford-blue-btn"
                    primary={true}
                    type={"submit"}
                    disabled={!formRenderProps.allowSubmit}
                  >
                    {localizationService.toLanguageString(
                      "custom.approve_event",
                      "approve_event"
                    )}
                  </Button>
                  &nbsp;
                  <Button
                    className="ford-blue-btn"
                    onClick={() => setDialogDisplayed("none")}>
                    {localizationService.toLanguageString(
                      "custom.cancel",
                      "cancel"
                    )}
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}
      {dialogDisplayed === "reschedule" && (
        <Dialog>
          <h3>Reschedule Event</h3>
          <br />
          <Form
            onSubmit={handleReschedule}
            ignoreModified={true}
            initialValues={{
              rescheduleDate: null,
            }}
            render={(formRenderProps) => (
              <FormElement horizontal={true}>
                <p>
                  Please select a new date for this event: <span style={{ fontWeight: 'bold' }}>{thisEvent?.event_name}</span> originally scheduled for <span style={{ fontWeight: 'bold' }}>{moment(thisEvent?.start_date).format('MM/DD/YYYY')}</span>.
                </p>
                <Field
                  component={DatePicker}
                  name="rescheduleDate"
                  label="New Event Date"
                  min={new Date(Date.now())} // Today
                  max={new Date('2026-12-31')} // December 31st, 2025
                  required={true}
                />
                {formError && <Error>{formError}</Error>}
                <DialogActionsBar>
                  <Button
                    className="ford-blue-btn"
                    primary={true}
                    type={"submit"}
                    disabled={!formRenderProps.allowSubmit || !formRenderProps.valueGetter('rescheduleDate')}
                  >
                    Reschedule Event
                  </Button>
                  &nbsp;
                  <Button
                    className="ford-blue-btn"
                    onClick={() => setDialogDisplayed("none")}>
                    {localizationService.toLanguageString(
                      "custom.cancel",
                      "cancel"
                    )}
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}
      {dialogDisplayed === "reimbursement" && (
        <Dialog>
          <h3>Approve Reimbursement</h3>
          <Form
            onSubmit={
              addressVerified
                ? onSubmitReimbursementApproval
                : onSubmitReimbursementApprovalForceAddress
            }
            ignoreModified={true}
            render={(formRenderProps) => (
              <FormElement horizontal={true}>
                <p>
                  Are you sure you want to approve this reimbursement? The
                  dealer will be notified immediately.
                </p>
                <Field
                  component={NumericTextBox}
                  id={"reimbursement_amount"}
                  childRef={ref}
                  name={"reimbursement_amount"}
                  format="c2"
                  label={localizationService.toLanguageString(
                    "custom.reimbursement_amount",
                    "Reimbursement Amount"
                  )}
                  required={true}
                />
                {!addressVerified && (
                  <>
                    <div className="address_unverified_text">
                      <p>
                        This retailer address could not be verified by USPS.
                        Please see below and confirm that the address is
                        correct. If not, select "Cancel" and "View/Edit
                        Dealer" to edit the address.
                      </p>
                      <p>
                        If the address is correct, press "Confirm Address" to
                        continue the reimbursement process.
                      </p>
                    </div>
                    <div className="dealer_address_cntr">
                      <p>
                        {dealer?.address} {dealer?.address2}
                      </p>
                      <p>
                        {dealer?.city}, {dealer?.state} {dealer?.zip}
                      </p>
                    </div>
                  </>
                )}
                {formError && <Error>{formError}</Error>}
                <DialogActionsBar>
                  <Button
                    className="ford-blue-btn"
                    primary={true}
                    type={"submit"}
                    disabled={!formRenderProps.allowSubmit}
                  >
                    {localizationService.toLanguageString(
                      "custom.approve_reimbursement",
                      "Approve Reimbursement"
                    )}
                  </Button>
                  &nbsp;
                  <Button
                    className="ford-blue-btn"
                    onClick={() => setDialogDisplayed("none")}>
                    {localizationService.toLanguageString(
                      "custom.cancel",
                      "cancel"
                    )}
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}
      {dialogDisplayed === "approveDonation" && (
        <Dialog className="approve_donation_dialog">
          <h3>Approve Donation</h3>
          <Form
            onSubmit={
              addressVerified
                ? onSubmitDonationApproval
                : onSubmitDonationApprovalForceAddress
            }
            ignoreModified={true}
            initialValues={{
              ...formValues,
              memo: preApprovalMemo || '',
            }}
            render={(formRenderProps) => (
              <FormElement horizontal={true}>
                <p>Event Notes (optional)</p>
                <Field
                  component={TextArea}
                  id={"event_summary"}
                  name={"event_summary"}
                  label={localizationService.toLanguageString(
                    "custom.event_summary",
                    "event_summary"
                  )}
                  required={false}
                />
                <p>Custom Memo <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666' }}>(Leave blank for default)</span></p>
                <Field
                  component={TextArea}
                  id={"memo"}
                  name={"memo"}
                  label="memo"
                  required={false}
                />
                <p>Total Donation Amount:</p>
                <Field
                  component={Input}
                  id={"check_amount"}
                  name={"check_amount"}
                  validator={dollarValidator}
                />
                <p>Valid Post-Drive Surveys:</p>
                <Field
                  component={Input}
                  id={"valid_post_count"}
                  name={"valid_post_count"}
                  validator={positiveNumberValidator}
                />
                {thisEvent?.bonus_event ? (
                  <>
                    <p>Valid Bonus Surveys:</p>
                    <Field
                      component={Input}
                      id={"bonus_valid_count"}
                      name={"bonus_valid_count"}
                      validator={positiveNumberValidator}
                    />
                  </>
                ) : null}

                {!addressVerified && (
                  <>
                    <div className="address_unverified_text">
                      <p>
                        This address could not be verified by USPS.
                        Please see below and confirm that the address is
                        correct. If not, select "Cancel" and "View/Edit
                        Dealer" to edit the address.
                      </p>
                      <p>
                        If the address is correct, press "Confirm Address" to
                        continue the donation process.
                      </p>
                    </div>
                    <div className="dealer_address_cntr">
                      <p>
                        {dealer?.address} {dealer?.address2}
                      </p>
                      <p>
                        {dealer?.city}, {dealer?.state} {dealer?.zip}
                      </p>
                    </div>
                  </>
                )}
                {formError && <Error>{formError}</Error>}
                <DialogActionsBar>
                  {addressVerified ? (
                    <Button
                      className="ford-blue-btn"
                      primary={true}
                      type={"submit"}
                      disabled={!formRenderProps.allowSubmit}
                    >
                      {localizationService.toLanguageString(
                        "custom.approve_donation",
                        "approve_donation"
                      )}
                    </Button>
                  ) : (
                    <Button
                      className="ford-blue-btn"
                      primary={true}
                      type={"submit"}
                      onClick={() => { }}
                      disabled={!formRenderProps.allowSubmit}
                    >
                      {localizationService.toLanguageString(
                        "custom.confirm_address",
                        "Confirm Address"
                      )}
                    </Button>
                  )}
                  &nbsp;
                  <Button
                    className="ford-blue-btn"
                    onClick={() => {
                      toggleAddressVerified(true);
                      setDialogDisplayed("none");
                    }}
                  >
                    {localizationService.toLanguageString(
                      "custom.cancel",
                      "cancel"
                    )}
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}
      {dialogDisplayed === "none" && (
        <div className="card-container" style={{ maxWidth: 700 }}>
          <h1 className="card-title" style={fontBold}>
            {localizationService.toLanguageString(
              "custom.event_details",
              "event_details"
            )}
          </h1>

          <div className="card-component">
            <br />
            <h3>
              <span style={fontBold}>Status: </span> {capitalize(thisEvent?.status)}
              {
                // Eligibility buttons
                thisEvent?.status === "new" &&
                login.user?.credentials.hasPermission('admin') && (
                  <p>
                    <Button
                      className="ford-blue-btn"
                      onClick={() => setDialogDisplayed("approveEligibility")}
                    >
                      Approve Eligibility
                    </Button>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <Button
                      className="ford-blue-btn"
                      onClick={rejectEligibility}>
                      Reject Eligibility
                    </Button>
                  </p>
                )
              }

              {thisEvent?.event_id && (
                <p>
                  <Button
                    className="ford-blue-btn"
                    onClick={() => doMagicLink()}>
                    Event Portal Login
                  </Button>
                </p>
              )}
              {thisEvent?.status === "event ended" && (
                <p>
                  <Button
                    className="ford-blue-btn"
                    onClick={() => setDialogDisplayed("approveDonation")}>
                    Approve Donation
                  </Button>
                </p>
              )}
            </h3>
            <hr />
            {formDisplayed === "none" && thisEvent?.event_id && (
              <>
                <h3 style={fontBold}>Event Details{" "}
                  <br />
                  <Button
                    className="ford-blue-btn"
                    /* Past events stay editable -- everything else on this form
                       is still needed after the event. Only the date is locked,
                       by eventDateLocked on the start_date field below. */
                    onClick={() => setFormDisplayed('event')} disabled={['cancelled', 'ineligible'].includes(thisEvent?.status)}>
                    View/Edit Event
                  </Button>
                {thisEvent.status === 'event ended' && <Button
                    className="ford-blue-btn"
                    /* Rescheduling is switched off while the date-change fix is
                       scoped -- see eventDateLocked above. Restore the commented
                       condition to turn it back on. */
                    onClick={() => setDialogDisplayed('reschedule')}
                    disabled={eventDateLocked /* || ['cancelled', 'ineligible'].includes(thisEvent?.status) || moment(thisEvent?.start_date).isAfter(moment()) */}>
                    Reschedule Event
                  </Button>}
                {thisEvent.status === 'event ended' && <span style={{ display: 'block', fontSize: 12, color: '#F28888', fontWeight: 400 }}>Rescheduling is temporarily disabled for events that have already taken place.</span>}
                  <Button
                    className="ford-red-btn"
                    onClick={() => setDialogDisplayed('cancel_confirm')} disabled={['cancelled', 'ineligible'].includes(thisEvent?.status)}>
                    Cancel Event
                  </Button>
                </h3>
                <section>
                  {/* dealer-typed values are title cased for display only */}
                  <span style={fontSemiBold}>Name: </span>{titleCase(thisEvent?.event_name)}
                  <br />
                  <span style={fontSemiBold}>Meridian Event ID: </span>{thisEvent.event_id}
                  <br />
                  <span style={fontSemiBold}>FFS Event ID: </span>{thisEvent.ffs_event_id}
                  <br />
                  <span style={fontSemiBold}>Event Survey URL: </span>{" "}
                  <a href={surveyLink} target="_blank" rel="noreferrer">
                    {surveyLink}
                  </a>
                  <br />
                  <span style={fontSemiBold}>Event Date: </span>{" "}
                  {moment(thisEvent.start_date).format("dddd, MMMM Do")}{" "}
                  {formatRange(thisEvent.start_time, thisEvent.end_time)}
                  <br />
                  <span style={fontSemiBold}>Start Time: </span>{thisEvent?.start_time ? (typeof thisEvent.start_time === 'string' ? moment(thisEvent.start_time, "HH:mm:ss").format("h:mm a") : moment(thisEvent.start_time).format("h:mm a")) : 'N/A'}
                  <br />
                  <span style={fontSemiBold}>End Time: </span>{thisEvent?.end_time ? (typeof thisEvent.end_time === 'string' ? moment(thisEvent.end_time, "HH:mm:ss").format("h:mm a") : moment(thisEvent.end_time).format("h:mm a")) : 'N/A'}
                  <br />
                  <span style={fontSemiBold}>Organization: </span>{titleCase(thisEvent?.charity_partner)}<br />
                  <span style={fontSemiBold}>Secondary Organization: </span>{titleCase(thisEvent?.secondary_charity) || 'N/A'}<br />
                  <span style={fontSemiBold}>Organization Category: </span>{/* set in the Approve Event dialog; events approved before it have neither */}
                  {organizationTypeLabel(thisEvent?.organization_type) || 'Not classified'}<br />
                  <span style={fontSemiBold}>Organization Subcategory: </span>{organizationSubcategoryLabel(thisEvent?.organization_type, thisEvent?.organization_subcategory) || 'Not classified'}<br />
                  <span style={fontSemiBold}>Event Location Type: </span>{thisEvent?.event_location_type}<br />
                  <span style={fontSemiBold}>Funding Purpose: </span>{sentenceCase(thisEvent?.funding_purpose)}<br />
                  <span style={fontSemiBold}>Funding Purpose Category: </span>{thisEvent?.funding_purpose_category}<br />
                  <span style={fontSemiBold}>Event Address: </span>{generateEventAddress()}<br />
                  <span style={fontSemiBold}>National Charity Partner: </span>{thisEvent?.nat_charity}<br />
                  {/* Set in the Approve Event dialog (editable under View/Edit Event), separate from the
                      dealer's pick above. Events approved before the dialog have no organization_type. */}
                  <span style={fontSemiBold}>Primary Org FBT Status: </span>
                  {thisEvent?.fbt_partner ? `Yes, ${thisEvent.fbt_partner}` : (thisEvent?.organization_type ? 'No' : 'Not classified')}<br />
                </section>
                <hr />
                <h3 style={fontBold}>Outreach Notes</h3>
                <section>
                  {generateOutreachNotes(thisEvent?.outreach_notes, thisEvent?.outreach_done)}
                </section>
                <hr />
                <h3 style={fontBold}>Banking Info</h3>
                <section>
                  {/* Falls back to the event's payee -- the duplicate under Event
                      Details was removed, so this is the only place it shows. */}
                  {(preApprovalCheckPayable || thisEvent?.check_payable) && (
                    <>
                      <span style={fontSemiBold}>Check Payable: </span>{titleCase(preApprovalCheckPayable || thisEvent?.check_payable)}
                      <br />
                    </>
                  )}
                  <span style={fontSemiBold}>Memo: </span>{preApprovalMemo}
                  <br />
                  <p style={{ fontWeight: 700, marginBottom: 0 }}>
                    Check Mailing Address:
                  </p>
                  {
                    thisEvent?.check_location === "The School address provided" ?
                      <p style={{ marginTop: 0 }}>
                        <span style={fontSemiBold}>Address: </span>{charity?.address_1}
                        <br />
                        <span style={fontSemiBold}>Apt/Suite/Other: </span>{charity?.address_2}
                        <br />
                        <span style={fontSemiBold}>City: </span>{charity?.city}
                        <br />
                        <span style={fontSemiBold}>State: </span>{charity?.state}
                        <br />
                        <span style={fontSemiBold}>Zip: </span>{charity?.postal_code}
                        <br />
                      </p>
                      :
                      <p style={{ marginTop: 0 }}>
                        <span style={fontSemiBold}>Address: </span>{dealer?.address}
                        <br />
                        <span style={fontSemiBold}>Apt/Suite/Other: </span>{dealer?.address2}
                        <br />
                        <span style={fontSemiBold}>City: </span>{dealer?.city}
                        <br />
                        <span style={fontSemiBold}>State: </span>{dealer?.state}
                        <br />
                        <span style={fontSemiBold}>Zip: </span>{dealer?.zip}
                        <br />
                      </p>
                  }
                  <Button
                    className="ford-blue-btn"
                    style={{ marginTop: 8 }}
                    disabled={['donation requested', 'donation approved'].includes(thisEvent?.status as string)}
                    onClick={() => {
                      setMemoText(preApprovalMemo);
                      setCheckPayableText(preApprovalCheckPayable);
                      setMemoDialogVisible(true);
                    }}
                  >
                    {preApprovalMemo || preApprovalCheckPayable ? 'Edit Banking Info' : 'Add Banking Info'}
                  </Button>
                </section>
                <hr />
              </>
            )}

            {formDisplayed === "event" && (
              <Form
                onSubmit={onSubmitUpdateEvent}
                initialValues={{
                  ...formValues,
                  ...thisEvent,
                  event_type: thisEvent?.event_type,
                  nat_charity: thisEvent?.nat_charity,
                  organization_type_label: organizationTypeLabel(thisEvent?.organization_type),
                  organization_subcategory_label: organizationSubcategoryLabel(thisEvent?.organization_type, thisEvent?.organization_subcategory),
                  // No answer is only implied once the event has been classified
                  fbt_status: thisEvent?.fbt_partner ? 'Yes' : (thisEvent?.organization_type ? 'No' : ''),
                  fbt_partner: thisEvent?.fbt_partner || '',
                  start_time: thisEvent?.start_time ? (typeof thisEvent.start_time === 'string' ? moment(thisEvent.start_time, "HH:mm:ss").toDate() : thisEvent.start_time) : undefined,
                  end_time: thisEvent?.end_time ? (typeof thisEvent.end_time === 'string' ? moment(thisEvent.end_time, "HH:mm:ss").toDate() : thisEvent.end_time) : undefined,
                }}
                render={(formRenderProps) => (
                  <FormElement horizontal={true} style={{ maxWidth: 700 }}>
                    <h3>Edit Event Plan</h3>
                    {formError && <Error>{formError}</Error>}
                    <Field
                      id={"event_name"}
                      name={"event_name"}
                      maxLength={30}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.event_name",
                        "event_name"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"funding_purpose_category"}
                      name={"funding_purpose_category"}
                      label={localizationService.toLanguageString(
                        "custom.funding_purpose_category",
                        "funding_purpose_category"
                      )}
                      validator={requiredValidator}
                      component={DropDownList}
                      data={[
                        "Community Development (Build / Restore Community Spaces)",
                        "Publicly Available Parks / Playgrounds / Spaces",
                        "Workforce Development (Vocational / Trades / Auto Tech)",
                        "Scholarships (Vocational / Trades / Entrepreneurship)",
                        "Small Business / Entrepreneurship Skill-Based Learning",
                        "Housing / Neighborhood Revitalization (e.g., Habitat for Humanity)",
                        "Hunger Relief / Food Security (e.g., Feeding America, World Central Kitchen)",
                        "Emergency / Disaster Preparedness, Response & Recovery (e.g.,  American Red Cross, Team Rubicon)"
                      ]}
                    />
                    <Field
                      id={"funding_purpose"}
                      name={"funding_purpose"}
                      label={localizationService.toLanguageString(
                        "custom.funding_purpose",
                        "funding_purpose"
                      )} 
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"start_date"}
                      name={"start_date"}
                      label={localizationService.toLanguageString(
                        "custom.event_date",
                        "event_date"
                      )}
                      childRef={dateRef}
                      format="MM/dd/yyyy"
                      min={moment().toDate()}
                      max={moment("2026-12-30").toDate()}
                      component={DatePicker}
                      disabled={eventDateLocked}
                      validator={requiredValidator}
                      onChange={(e) => {
                        if (moment(e.value).diff(moment(), 'days') < 30) {
                          setShowRRDMessage(true)
                        } else {
                          setShowRRDMessage(false)
                        }
                      }}
                    />
                    {eventDateLocked && <p style={{ fontSize: 12, color: '#F28888' }}>The event date cannot be changed once the event has passed. Contact the development team if a date change is required.</p>}
                    {/* {(showRRDMessage) && <p style={{ color: 'red', fontStyle: 'bold' }}>This new event date is within 30 days. Please communicate any changes to RRD.</p>} */}
                    <Field
                      id={"start_time"}
                      name={"start_time"}
                      label={localizationService.toLanguageString(
                        "custom.start_time",
                        "Start Time"
                      )}
                      childRef={startTimeRef}
                      format="hh:mm a"
                      component={TimePicker}
                      validator={(value, valueGetter) => {
                        if (!value) return "";
                        const endTime = valueGetter("end_time");
                        if (endTime) {
                          const startMoment = moment(value);
                          const endMoment = moment(endTime);
                          if (endMoment.isSameOrBefore(startMoment)) {
                            return "Start time must be before end time";
                          }
                        }
                        return "";
                      }}
                    />
                    <Field
                      id={"end_time"}
                      name={"end_time"}
                      label={localizationService.toLanguageString(
                        "custom.end_time",
                        "End Time"
                      )}
                      childRef={endTimeRef}
                      format="hh:mm a"
                      component={TimePicker}
                      validator={(value, valueGetter) => {
                        if (!value) return "";
                        const startTime = valueGetter("start_time");
                        if (startTime) {
                          const startMoment = moment(startTime);
                          const endMoment = moment(value);
                          if (endMoment.isSameOrBefore(startMoment)) {
                            return "End time must be after start time";
                          }
                        }
                        return "";
                      }}
                    />
                    <Field
                      id={"secondary_charity"}
                      name={"secondary_charity"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.secondary_charity",
                        "Secondary Organization"
                      )}
                      validator={secondaryOrgDeleted || !thisEvent?.secondary_charity ? [] : requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"check_payable"}
                      name={"check_payable"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.check_payable",
                        "Check Payable To"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Button
                      className="ford-blue-btn"
                      onClick={(e) => {
                        e.preventDefault()
                        formRenderProps.onChange("secondary_charity", { value: '' });
                        deleteSecondaryOrg(true)
                      }}
                      style={{ backgroundColor: 'red', color: 'white', width: '300px' }}
                      disabled={!thisEvent?.secondary_charity || secondaryOrgDeleted}
                    >
                      {secondaryOrgDeleted ? 'Organization Deleted' : 'Delete Existing Secondary Organization'}
                    </Button>
                    <p style={{ margin: 0, padding: 0, textAlign: 'left', fontSize: 10 }}>Changes are not permanent until form is saved, so if deleted on accident, just refresh the page.</p>
                    <Field
                      id={"nat_charity"}
                      name={"nat_charity"}
                      label="National Charity Partner"
                      component={DropDownList}
                      data={[
                        "Feeding America", 
                        "American Red Cross", 
                        "Team Rubicon", 
                        "Habitat for Humanity",
                        "World Central Kitchen",
                        "June Campaign"
                      ]}
                    />
                    {/* D4URX-2760: set in the Approve Event dialog, editable here */}
                    <Field
                      id={"organization_type_label"}
                      name={"organization_type_label"}
                      label="Organization Category"
                      component={DropDownList}
                      data={ORGANIZATION_TYPES.map((t) => t.label)}
                      onChange={() => formRenderProps.onChange("organization_subcategory_label", { value: '' })}
                    />
                    {formRenderProps.valueGetter("organization_type_label") && (
                      <Field
                        id={"organization_subcategory_label"}
                        name={"organization_subcategory_label"}
                        label="Organization Subcategory"
                        component={DropDownList}
                        data={(ORGANIZATION_SUBCATEGORIES[ORGANIZATION_TYPES.find((t) => t.label === formRenderProps.valueGetter("organization_type_label"))?.value || ''] || []).map((sub) => sub.label)}
                        validator={requiredValidator}
                      />
                    )}
                    <Field
                      id={"fbt_status"}
                      name={"fbt_status"}
                      label="Primary Org FBT Status"
                      component={DropDownList}
                      data={['Yes', 'No']}
                      onChange={(e) => { if (e.target.value !== 'Yes') formRenderProps.onChange("fbt_partner", { value: '' }); }}
                    />
                    {formRenderProps.valueGetter("fbt_status") === 'Yes' && (
                      <Field
                        id={"fbt_partner"}
                        name={"fbt_partner"}
                        label="FBT Partner"
                        component={DropDownList}
                        data={FBT_PARTNERS}
                        validator={requiredValidator}
                      />
                    )}
                    <Field
                      id={'event_location_type'}
                      name={'event_location_type'}
                      data={['The School', 'The Dealership', 'Other']}
                      // value={thisEvent?.event_location_type}
                      label={`Event Location`}
                      // validator={requiredValidator}
                      hint={`Please select the option that best describes the location of the event. If "Other", the address fields below are required.`}
                      component={DropDownList}
                      defaultValue={thisEvent?.event_location_type}
                      onChange={(e) => {
                        if (e.target.value === 'Other') {
                          setDisplayOtherAddressForm(true)
                        } else {
                          setDisplayOtherAddressForm(false)
                        }
                      }}
                    />
                    {(displayOtherAddressForm || (thisEvent?.event_location_type === 'Other' && !['The School', 'The Dealership'].includes(formRenderProps.valueGetter("event_location_type")))) &&
                      <div>
                        <Field
                          id={"address"}
                          name={"address"}
                          // type={'input'}
                          label={localizationService.toLanguageString(
                            "custom.address",
                            "address"
                          )}
                          validator={displayOtherAddressForm ? address1Validator : []}
                          component={Input}
                        />
                        <Field
                          id={"city"}
                          name={"city"}
                          // type={'input'}
                          label={localizationService.toLanguageString(
                            "custom.city",
                            "city"
                          )}
                          validator={displayOtherAddressForm ? cityValidator : []}
                          component={Input}
                        />
                        <Field
                          id={"state"}
                          name={"state"}
                          // type={'input'}
                          label={localizationService.toLanguageString(
                            "custom.state",
                            "state"
                          )}
                          validator={displayOtherAddressForm ? stateValidator : []}
                          component={Input}
                        />
                        <Field
                          id={"zip_code"}
                          name={"zip_code"}
                          // type={'input'}
                          label={localizationService.toLanguageString(
                            "custom.zip",
                            "zip"
                          )}
                          validator={displayOtherAddressForm ? zipValidator : []}
                          component={Input}
                        />
                      </div>
                    }
                    <hr />
                    <p style={{ color: 'red', fontStyle: 'bold' }}>This event has {registrants?.completed_registrations} pre-registrations.</p>
                    {moment(thisEvent?.start_date).diff(moment(), 'days') < 30 && <p style={{ color: 'red', fontStyle: 'bold' }}>This event is (or was) scheduled within 30 days. Please communicate any Event Date changes to RRD.</p>}
                    <div className={"k-form-buttons"}>
                      <Button
                        className="ford-blue-btn"
                        primary={true}
                        type={"submit"}
                        disabled={!formRenderProps.allowSubmit}
                      >
                        {localizationService.toLanguageString(
                          "custom.save",
                          "save"
                        )}
                      </Button>

                      <Button
                        className="ford-blue-btn"
                        onClick={() => {
                          setFormDisplayed("none");
                          deleteSecondaryOrg(false);
                          setDisplayOtherAddressForm(false);
                        }}>
                        {localizationService.toLanguageString(
                          "custom.cancel",
                          "cancel"
                        )}
                      </Button>
                    </div>
                  </FormElement>
                )}
              />
            )}

            {formDisplayed === "none" && (
              <>
                <h3 style={fontBold}>
                  Dealer{" "}
                  <Button
                    className="ford-blue-btn"
                    onClick={() => setFormDisplayed("dealer")}>
                    View/Edit Dealer
                  </Button>
                </h3>
                <section>
                  <span style={fontSemiBold}>Name: </span>{dealer?.dealer_name}
                  <br />
                  <span style={fontSemiBold}>Sales Code: </span>{dealer?.parts_code}
                  <br />
                  <span style={fontSemiBold}>First Time Dealer: </span>{dealer?.firstTimeDealerGridCell}
                  <br />
                  <span style={fontSemiBold}>Contact: </span>{dealer?.primary_contact}
                  <br />
                  <span style={fontSemiBold}>Contact Email: </span>{dealer?.contact_email}
                  <br />
                  <span style={fontSemiBold}>Contact Phone: </span>{dealer?.contact_phone}
                  <br />
                  <details open>
                    <summary style={fontSemiBold}>Additional Contacts</summary>
                    <span style={fontSemiBold}>Secondary Contact: </span>{dealer?.secondary_contact || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Secondary Contact Email: </span>{dealer?.secondary_contact_email || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Secondary Contact Phone: </span>{dealer?.secondary_contact_phone || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Tertiary Contact: </span>{dealer?.tertiary_contact || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Tertiary Contact Email: </span>{dealer?.tertiary_contact_email || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Tertiary Contact Phone: </span>{dealer?.tertiary_contact_phone || 'N/A'}
                  </details>
                  <span style={fontSemiBold}>1st Time Dealer: </span>{dealer?.firstTimeDealerGridCell}
                  <br />
                  <span style={fontSemiBold}>Hispanic: </span>{dealer?.hispanicReadable}
                  <br />

                  {/* ARCHIVED (D4URX-2752): Dealer Notes are hidden everywhere in the admin; code kept so it can be restored.
                     To restore: delete these two header lines and the closing comment line, and un-escape any star-backslash-slash.
                  {/* Interactive Dealer Notes Section - Inline with other fields *\/}
                  <div style={{ marginTop: 10, marginBottom: 10, display: 'flex', alignItems: 'center' }}>
                    <span style={fontSemiBold}>Dealer Notes: </span>
                    
                    {(() => {
                      const dealerNotes = localDealerNotes.length > 0 ? localDealerNotes : dealer?.dealer_notes;
                      let notesCount = 0;
                      let parsedNotes: any[] = [];

                      if (dealerNotes) {
                        if (typeof dealerNotes === 'string') {
                          try {
                            parsedNotes = JSON.parse(dealerNotes);
                            notesCount = Array.isArray(parsedNotes) ? parsedNotes.length : 0;
                          } catch {
                            notesCount = 0;
                          }
                        } else if (Array.isArray(dealerNotes)) {
                          parsedNotes = dealerNotes;
                          notesCount = dealerNotes.length;
                        }
                      }

                      const openDealerNotesModal = () => {
                        // Try to get dealer ID from different sources
                        let dealerId = dealer?.dealer_id || thisEvent?.dealer_id || null;
                        
                        // Check if dealer data is loaded
                        if (!dealerId) {
                          setErrorMessage('Dealer data not loaded. Please refresh the page and try again.');
                          return;
                        }
                        
                        setSelectedDealerNotes(localDealerNotes.length > 0 ? localDealerNotes : parsedNotes || []);
                        setSelectedDealerNameForNotes(dealer?.dealer_name || '');
                        setSelectedDealerIdForNotes(dealerId);
                        setDealerNotesModalDealerId(dealerId);
                        setShowAddDealerNoteInModal(false);
                        setNewDealerNoteTextInModal('');
                        setDealerNotesModalVisible(true);
                      };

                      const openAddDealerNoteModal = () => {
                        // Try to get dealer ID from different sources
                        let dealerId = dealer?.dealer_id || thisEvent?.dealer_id || null;
                        
                        // Check if dealer data is loaded
                        if (!dealerId) {
                          setErrorMessage('Dealer data not loaded. Please refresh the page and try again.');
                          return;
                        }
                        
                        setSelectedDealerForNote(dealer);
                        setSelectedDealerNameForNote(dealer?.dealer_name || '');
                        setSelectedDealerForNoteId(dealerId);
                        setNewDealerNoteText('');
                        setAddDealerNoteModalVisible(true);
                      };

                      if (notesCount > 0) {
                        return (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ fontSize: "14px", marginLeft: "8px" }}>Notes({notesCount})</span>
                            <span
                              className={'k-icon k-i-edit'}
                              style={{ color: '#666', fontSize: '14px', cursor: 'pointer', marginLeft: "8px" }}
                              onClick={openDealerNotesModal}
                              title="Edit notes"
                            />
                          </div>
                        );
                      } else {
                        return (
                          <button
                            className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                            style={{ 
                              backgroundColor: "#007bff",
                              borderColor: "#007bff",
                              color: "white",
                              marginLeft: "8px"
                            }}
                            onClick={openAddDealerNoteModal}
                            title="Add note"
                          >
                            Add Note
                          </button>
                        );
                      }
                    })()}
                  </div>
                  
                  {/* Notes Preview - Show up to 5 notes *\/}
                  {(() => {
                    const dealerNotes = localDealerNotes.length > 0 ? localDealerNotes : dealer?.dealer_notes;
                    let parsedNotes: any[] = [];

                    if (dealerNotes) {
                      if (typeof dealerNotes === 'string') {
                        try {
                          parsedNotes = JSON.parse(dealerNotes);
                        } catch {
                          parsedNotes = [];
                        }
                      } else if (Array.isArray(dealerNotes)) {
                        parsedNotes = dealerNotes;
                      }
                    }

                    if (parsedNotes.length > 0) {
                      // Show up to 5 most recent notes
                      const notesToShow = parsedNotes
                        .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at))) // Sort by oldest first (matching Event.tsx)
                        .slice(0, 5);

                      return (
                        <div style={{ marginLeft: "0px" }}>
                          {notesToShow.map((note: any, index: number) => {
                            const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                            return (
                              <div key={index} style={{ 
                                borderBottom: "1px solid lightgrey", 
                                paddingBottom: "4px", 
                                marginBottom: "4px" 
                              }}>
                                {formattedDateTime} - {note.created_by_name || note.created_by}: {note.note}
                              </div>
                            );
                          })}
                          {parsedNotes.length > 5 && (
                            <div style={{ 
                              fontStyle: "italic", 
                              color: "#666",
                              marginTop: "8px"
                            }}>
                              ... and {parsedNotes.length - 5} more notes
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  */}

                  {/* <span style={fontSemiBold}>Notes: </span>{generateDealerNotes(dealer?.dealer_notes)} */}
                </section>
                <hr />
                <h3 style={fontBold}>Kit Shipment</h3>
                {/* The dealer's "ship kit to" dropdown answer, shown above the address it selects */}
                <p style={{ marginBottom: 0 }}>
                  <span style={fontSemiBold}>Ship To: </span>
                  {/dealership/i.test(thisEvent?.kit_location || '') ? 'The Dealership' : 'The Organization'}
                </p>
                {
                  /* Same rule as the RRD Kit Delivery sheet (kitDeliveryData in d4urx-api):
                     dealer address only when the dealer picked the dealership, otherwise the
                     organization. The form's option is "The Organization address provided";
                     older events still say "The School address provided". */
                  !/dealership/i.test(thisEvent?.kit_location || '') ?
                    <section style={{ marginTop: 0 }}>
                      <span style={fontSemiBold}>Address: </span>{charity?.address_1}
                      <br />
                      <span style={fontSemiBold}>Apt/Suite/Other: </span>{charity?.address_2}
                      <br />
                      <span style={fontSemiBold}>City: </span>{charity?.city}
                      <br />
                      <span style={fontSemiBold}>State: </span>{charity?.state}
                      <br />
                      <span style={fontSemiBold}>Zip: </span>{charity?.postal_code}
                      <br />
                    </section>
                    :
                    <p style={{ marginTop: 0 }}>
                      <span style={fontSemiBold}>Address: </span>{dealer?.address}
                      <br />
                      <span style={fontSemiBold}>Apt/Suite/Other: </span>{dealer?.address2}
                      <br />
                      <span style={fontSemiBold}>City: </span>{dealer?.city}
                      <br />
                      <span style={fontSemiBold}>State: </span>{dealer?.state}
                      <br />
                      <span style={fontSemiBold}>Zip: </span>{dealer?.zip}
                      <br />
                    </p>
                }
                <hr />
              </>
            )}
            {formDisplayed === "dealer" && (
              <Form
                onSubmit={onSubmitDealer}
                initialValues={{
                  ...formValues,
                  ...dealer,
                }}
                render={(formRenderProps) => (
                  <FormElement horizontal={true} style={{ maxWidth: 700 }}>
                    <h3>Edit Dealer</h3>
                    {formError && <Error>{formError}</Error>}
                    <Field
                      id={"dealer_name"}
                      name={"dealer_name"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.dealer_name",
                        "dealer_name"
                      )}
                      validator={requiredValidator}
                      component={Input}
                      maxLength={50}
                    />
                    <Field
                      id={"parts_code"}
                      name={"parts_code"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.sales_code",
                        "sales_code"
                      )}
                      component={Input}
                      disabled={true}
                      minLength={5}
                      maxLength={5}
                    />
                    <Field
                      id={"address"}
                      name={"address"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.address",
                        "address"
                      )}
                      validator={address1Validator}
                      component={Input}
                    />
                    <Field
                      id={"address2"}
                      name={"address2"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.address2",
                        "address2"
                      )}
                      component={Input}
                    />
                    <Field
                      id={"city"}
                      name={"city"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.city",
                        "city"
                      )}
                      validator={cityValidator}
                      component={Input}
                    />
                    <Field
                      id={"state"}
                      name={"state"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.state",
                        "state"
                      )}
                      validator={stateValidator}
                      component={Input}
                    />
                    <Field
                      id={"zip"}
                      name={"zip"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.zip",
                        "zip"
                      )}
                      validator={zipValidator}
                      component={Input}
                    />
                    {/* Dealer phone hidden for now
                    <Field
                      id={"phone"}
                      name={"phone"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.dealershipPhone",
                        "dealershipPhone"
                      )}
                      validator={phoneValidator}
                      component={Input}
                    />
                    */}

                    <Field
                      id={"primary_contact"}
                      name={"primary_contact"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.primary_contact",
                        "primary_contact"
                      )}
                      validator={requiredValidator}
                      component={Input}
                      maxLength={50}
                    />
                    <Field
                      id={"contact_phone"}
                      name={"contact_phone"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.contact_phone",
                        "contact_phone"
                      )}
                      validator={phoneValidator}
                      component={Input}
                    />
                    <Field
                      id={"contact_email"}
                      name={"contact_email"}
                      type={"email"}
                      autoComplete="username"
                      placeholder={"e.g.: peter@gmail.com"}
                      label={localizationService.toLanguageString(
                        "custom.contactEmail",
                        "Email"
                      )}
                      validator={emailValidator}
                      required={true}
                      component={Input}
                    />
                    <Field
                                            id={"secondary_contact"}
                                            name={"secondary_contact"}
                                            label={localizationService.toLanguageString(
                                                "custom.secondary_contact",
                                                "Secondary Contact"
                                            )}
                                            component={Input}
                                            required={true}
                                            validator={requiredValidator}
                                        />
                                        <Field
                                            id={"secondary_contact_email"}
                                            name={"secondary_contact_email"}
                                            label={localizationService.toLanguageString(
                                                "custom.secondary_contact_email",
                                                "Secondary Contact Email"
                                            )}
                                            validator={emailValidator}
                                            component={Input}
                                            required={true}
                                        />
                                        <Field
                                            id={"secondary_contact_phone"}
                                            name={"secondary_contact_phone"}
                                            label={localizationService.toLanguageString(
                                                "custom.secondary_contact_phone",
                                                "Secondary Contact Phone"
                                            )}
                                            validator={phoneValidator}
                                            component={Input}
                                            required={true}
                                        />
                                        <Field
                                            id={"tertiary_contact"}
                                            name={"tertiary_contact"}
                                            label={localizationService.toLanguageString(
                                                "custom.tertiary_contact",
                                                "Tertiary Contact"
                                            )}
                                            component={Input}
                                            required={true}
                                            validator={requiredValidator}
                                        />
                                        <Field
                                            id={"tertiary_contact_email"}
                                            name={"tertiary_contact_email"}
                                            label={localizationService.toLanguageString(
                                                "custom.tertiary_contact_email",
                                                "Tertiary Contact Email"
                                            )}
                                            validator={emailValidator}
                                            component={Input}
                                            required={true}
                                        />
                                        <Field
                                            id={"tertiary_contact_phone"}
                                            name={"tertiary_contact_phone"}
                                            label={localizationService.toLanguageString(
                                                "custom.tertiary_contact_phone",
                                                "Tertiary Contact Phone"
                                            )}
                                            validator={phoneValidator}
                                            component={Input}
                                            required={true}
                                        />
                    <Field
                      id={"password"}
                      name={"password"}
                      autoComplete="new-password"
                      type={"password"}
                      label={localizationService.toLanguageString(
                        "custom.password",
                        "Password"
                      )}
                      validator={newPasswordValidator}
                      component={Input}
                    />
                    <Field
                      id={"confirm_password"}
                      name={"confirm_password"}
                      type={"password"}
                      autoComplete="new-password"
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.confirmPassword",
                        "Confirm Password"
                      )}
                      validator={passwordMatchValidator}
                      component={Input}
                    />
                    {/* ARCHIVED (D4URX-2752): Dealer Notes are hidden everywhere in the admin; code kept so it can be restored.
                       To restore: delete these two header lines and the closing comment line, and un-escape any star-backslash-slash.
                    <Field
                      id={'new_dealer_note'}
                      name={'new_dealer_note'}
                      label={localizationService.toLanguageString('custom.dealerNote', 'New Dealer Note')}
                      // validator={passwordMatchValidator}
                      component={Input}
                    />
                    */}
                    <hr />
                    <div className={"card-buttons"}>
                      <Button
                        className="ford-blue-btn"
                        type={"button"}
                        onClick={() => setFormDisplayed("none")}
                      >
                        {localizationService.toLanguageString(
                          "custom.cancel",
                          "cancel"
                        )}
                      </Button>
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <Button
                        className="ford-blue-btn"
                        primary={true}
                        type={"submit"}
                        disabled={!formRenderProps.allowSubmit}
                      >
                        {localizationService.toLanguageString(
                          "custom.update",
                          "update"
                        )}
                      </Button>
                    </div>
                  </FormElement>
                )}
              />
            )}

            {/* D4URX-2752: per-event Dealer Feedback, replacing the archived Dealer Notes above */}
            {formDisplayed === "none" && thisEvent?.event_id && (
              <>
                <h3 style={fontBold}>Dealer Feedback</h3>
                <DealerFeedbackSection eventId={thisEvent.event_id} feedback={thisEvent.dealer_feedback || []} />
                <hr />
              </>
            )}

            {formDisplayed === "none" && (
              <>
                <h3 style={fontBold}>
                  Organization{" "}
                  <Button
                    className="ford-blue-btn"
                    onClick={() => setFormDisplayed("charity")}>
                    View/Edit Organization
                  </Button>
                </h3>
                <details open>
                  <summary style={fontSemiBold}>Organization Details</summary>
                  <section>
                    <span style={fontSemiBold}>Name: </span>{titleCase(charity?.name)}
                    <br />
                    <span style={fontSemiBold}>Address: </span>
                    {titleCase(charity?.address_1)} {titleCase(charity?.address_2)} {titleCase(charity?.city)}, {charity?.state} {charity?.postal_code}
                    <br />
                    <span style={fontSemiBold}>Contact: </span>{titleCase(charity?.contact_name) || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Contact Phone: </span>{charity?.contact_phone_number || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Contact Email: </span>{charity?.contact_email || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Secondary Contact: </span>{titleCase(charity?.secondary_contact_name) || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Secondary Contact Phone: </span>{charity?.secondary_contact_phone_number || 'N/A'}
                    <br />
                    <span style={fontSemiBold}>Secondary Contact Email: </span>{charity?.secondary_contact_email || 'N/A'}
                  </section>
                </details>
                <hr />
              </>
            )}
            {formDisplayed === "none" && thisEvent?.status && (
              <>
                <h3 style={fontBold}>
                  Donation Details{" "}
                </h3>

                <p>
                  <span style={fontSemiBold}>Donation Amount: </span>{" "}
                  {
                    thisEvent.check_amount_pretty
                  }
                  <br />
                </p>
                <hr />
              </>
            )}
            {formDisplayed === "none" && thisEvent?.status && (
              <>
                <h3 style={{ fontWeight: 700, marginBottom: 10 }}>
                  Resend Emails{" "}
                </h3>
                {(() => {
                  const emailHistory = generateEmailHistorySummary();
                  if (emailHistory.length > 0) {
                    return (
                      <div style={{ 
                        marginBottom: 20, 
                        padding: '10px 15px', 
                        backgroundColor: '#f8f9fa', 
                        border: '1px solid #dee2e6', 
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: '8px', color: '#495057' }}>
                          Email History:
                        </div>
                        <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          {emailHistory.map((email, index) => (
                            <div key={index} style={{ 
                              marginBottom: '6px', 
                              paddingBottom: '6px',
                              borderBottom: index < emailHistory.length - 1 ? '1px solid #e9ecef' : 'none'
                            }}>
                              <span style={{ fontWeight: 500, color: '#007bff' }}>{email.type}</span>
                              {' - '}
                              <span style={{ color: '#6c757d' }}>
                                {moment(email.sent_at).tz('America/New_York').format('MMM DD, YYYY h:mm a')} (EST)
                              </span>
                              {email.sent_to && email.sent_to.length > 0 && (
                                <span style={{ color: '#495057', marginLeft: '8px' }}>
                                  → {email.sent_to.join(', ')}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div style={{ 
                      marginBottom: 20, 
                      padding: '10px 15px', 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #dee2e6', 
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#6c757d',
                      fontStyle: 'italic'
                    }}>
                      No emails have been sent yet.
                    </div>
                  );
                })()}
                <Form
                  onSubmit={onSubmitSendEmails}
                  ref={formRef}
                  // initialValues={{
                  //   ...formValues,
                  //   ...charity,
                  // }}
                  render={(formRenderProps) => (
                    <FormElement horizontal={true} style={{ maxWidth: 700 }}>
                      <Field
                        id={'email_type'}
                        name={'email_type'}
                        label={localizationService.toLanguageString('custom.email_type', 'Email Type')}
                        data={[
                          { id: 'pre_event', text: 'Pre-Event Info (training)' },
                          { id: 'event_approved', text: 'Event Approved' },
                          { id: 'kit_delivery', text: 'Kit Tracking Info' },
                        ]}
                        textField="text"
                        dataItemKey="id"
                        required={true}
                        validator={requiredValidator}
                        component={DropDownList}
                        onChange={(e) => {
                          setEmailType(e.target.value);
                        }}
                      />
                      {generateLastSentText(selectedEmailType)}
                      <Field
                        id={'send_to'}
                        name={'send_to'}
                        label={localizationService.toLanguageString('custom.send_to', 'Send To')}
                        required={true}
                        validator={requiredValidator}
                        component={Input}
                      />
                      <p className={"send-emails-subtext"}>Separate each address with a comma</p>
                      {errorMessage ? <p style={{ color: 'red' }}>{errorMessage}</p> : null}
                      {successMessage ? <p style={{ color: 'green' }}>{successMessage}</p> : null}
                      <div className={"card-buttons"}>
                        <Button
                          className="ford-blue-btn"
                          primary={true}
                          type={"submit"}
                        // disabled={!formRenderProps.allowSubmit || isLoading || ['cancelled', 'ineligible'].includes(thisEvent?.status) || moment(thisEvent?.start_date).endOf('day').isBefore(moment())}
                        >
                          {isLoading ? 'Sending...' : 'Send Email(s)'}
                        </Button>
                        {['cancelled', 'ineligible'].includes(thisEvent?.status) && <p style={{ fontSize: 12, color: '#F28888' }}>resending emails is disabled due to invalid event status ({thisEvent?.status}).</p>}
                      </div>
                    </FormElement>
                  )}
                ></Form>
              </>
            )}
            {formDisplayed === "charity" && (
              <Form
                onSubmit={onSubmitCharity}
                initialValues={{
                  ...formValues,
                  ...charity,
                }}
                render={(formRenderProps) => (
                  <FormElement horizontal={false} style={{ maxWidth: 700 }}>
                    <h3>Edit Organization</h3>
                    {formError && <Error>{formError}</Error>}
                    <Field
                      id={"name"}
                      name={"name"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.charity_name",
                        "charity_name"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />

                    <Field
                      id={"address_1"}
                      name={"address_1"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.address_1",
                        "address_1"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"address_2"}
                      name={"address_2"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.address_2",
                        "address_2"
                      )}
                      component={Input}
                    />
                    <Field
                      id={"city"}
                      name={"city"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.city",
                        "city"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"state"}
                      name={"state"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.state",
                        "state"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"postal_code"}
                      name={"postal_code"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.zip",
                        "zip"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"contact_name"}
                      name={"contact_name"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.contact_name",
                        "contact_name"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"contact_phone_number"}
                      name={"contact_phone_number"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.contact_phone_number",
                        "contact_phone_number"
                      )}
                      required={true}
                      validator={phoneValidator}
                      mask="000-000-0000"
                      component={MaskedTextBox}
                    />
                    <Field
                      id={"contact_email"}
                      name={"contact_email"}
                      type={"email"}
                      placeholder={"e.g.: peter@gmail.com"}
                      label={localizationService.toLanguageString(
                        "custom.contact_email",
                        "Email"
                      )}
                      validator={emailValidator}
                      required={true}
                      component={Input}
                    />
                    <Field
                      id={"secondary_contact_name"}
                      name={"secondary_contact_name"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.secondary_contact_name",
                        "Secondary Contact Name"
                      )}
                      validator={requiredValidator}
                      component={Input}
                    />
                    <Field
                      id={"secondary_contact_phone_number"}
                      name={"secondary_contact_phone_number"}
                      // type={'input'}
                      label={localizationService.toLanguageString(
                        "custom.secondary_contact_phone_number",
                        "Secondary Contact Phone Number"
                      )}
                      required={true}
                      validator={phoneValidator}
                      mask="000-000-0000"
                      component={MaskedTextBox}
                    />
                    <Field
                      id={"secondary_contact_email"}
                      name={"secondary_contact_email"}
                      type={"email"}
                      placeholder={"e.g.: peter@gmail.com"}
                      label={localizationService.toLanguageString(
                        "custom.secondary_contact_email",
                        "Secondary Contact Email"
                      )}
                      validator={emailValidator}
                      required={true}
                      component={Input}
                    />
                    <hr />
                    <div className={"card-buttons"}>
                      <Button
                        className="ford-blue-btn"
                        type={"button"}
                        onClick={() => setFormDisplayed("none")}
                      >
                        {localizationService.toLanguageString(
                          "custom.cancel",
                          "cancel"
                        )}
                      </Button>
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <Button
                        className="ford-blue-btn"
                        primary={true}
                        type={"submit"}
                        disabled={!formRenderProps.allowSubmit}
                      >
                        {localizationService.toLanguageString(
                          "custom.update",
                          "update"
                        )}
                      </Button>
                    </div>
                  </FormElement>
                )}
              />
            )}
          </div>
        </div>
      )}
      {/* Add Outreach Note Modal */}
      {addOutreachNoteModalVisible && (
        <Dialog
          title={`Add Outreach Note - ${dealer?.dealer_name}`}
          onClose={() => {
            setAddOutreachNoteModalVisible(false);
            setNewOutreachNoteText('');
          }}
          width={500}
          height={300}
        >
          <div style={{ padding: "20px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Note:
            </label>
            <textarea
              value={newOutreachNoteText}
              onChange={(e) => setNewOutreachNoteText(e.target.value)}
              placeholder="Enter your outreach note here..."
              style={{
                width: "calc(100% - 20px)",
                height: "120px",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                resize: "vertical",
                marginRight: "10px"
              }}
            />
          </div>
          <DialogActionsBar>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => {
                setAddOutreachNoteModalVisible(false);
                setNewOutreachNoteText('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              style={{
                backgroundColor: "#007bff",
                borderColor: "#007bff",
                color: "white"
              }}
              onClick={handleSaveOutreachNote}
              disabled={!newOutreachNoteText.trim()}
            >
              Save Note
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* View/Edit Outreach Notes Modal */}
      {outreachNotesModalVisible && (
        <Dialog
          title={`Outreach Notes - ${dealer?.dealer_name}`}
          onClose={() => {
            setOutreachNotesModalVisible(false);
            setShowAddNoteInModal(false);
            setNewOutreachNoteTextInModal('');
          }}
          width={600}
          height={showAddNoteInModal ? 550 : 400}
        >
          <div style={{ padding: "20px" }}>
            {/* Add Note Section - Moved to top */}
            {showAddNoteInModal ? (
              <div style={{ 
                marginBottom: "20px", 
                padding: "15px", 
                border: "2px solid #007bff", 
                borderRadius: "5px",
                backgroundColor: "#f8f9fa"
              }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                  Add New Note:
                </label>
                <textarea
                  value={newOutreachNoteTextInModal}
                  onChange={(e) => setNewOutreachNoteTextInModal(e.target.value)}
                  placeholder="Enter your outreach note here..."
                  style={{
                    width: "100%",
                    height: "80px",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    resize: "vertical",
                    boxSizing: "border-box"
                  }}
                />
                <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                  <Button
                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                    style={{
                      backgroundColor: "#007bff",
                      borderColor: "#007bff",
                      color: "white"
                    }}
                    onClick={handleSaveOutreachNoteInModal}
                    disabled={!newOutreachNoteTextInModal.trim()}
                  >
                    Save Note
                  </Button>
                  <Button
                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                    onClick={() => {
                      setShowAddNoteInModal(false);
                      setNewOutreachNoteTextInModal('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                style={{ 
                  marginBottom: "20px",
                  backgroundColor: "#007bff",
                  borderColor: "#007bff",
                  color: "white"
                }}
                onClick={() => setShowAddNoteInModal(true)}
              >
                Add Note
              </Button>
            )}

            {selectedEventNotes.length > 0 ? (
              selectedEventNotes.map((note, index) => (
                <div 
                  key={index} 
                  style={{ 
                    marginBottom: "20px", 
                    padding: "15px", 
                    border: "1px solid #ddd", 
                    borderRadius: "5px",
                    backgroundColor: "#f9f9f9"
                  }}
                >
                  {editingNoteIndex === index ? (
                    <div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Note:</strong>
                        <textarea
                          value={editedNoteText}
                          onChange={(e) => setEditedNoteText(e.target.value)}
                          style={{
                            width: "100%",
                            height: "80px",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            marginTop: "5px",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
                        <span style={{ marginRight: "15px" }}>
                          <strong>Created:</strong> {note.created_at}
                        </span>
                        <span>
                          <strong>By:</strong> {note.created_by}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <Button
                          className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                          style={{
                            backgroundColor: "#007bff",
                            borderColor: "#007bff",
                            color: "white"
                          }}
                          onClick={() => handleSaveEditedOutreachNote(note.created_at)}
                          disabled={!editedNoteText.trim()}
                        >
                          Save
                        </Button>
                        <Button
                          className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                          onClick={() => {
                            setEditingNoteIndex(null);
                            setEditedNoteText('');
                            setOriginalNoteContent('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: "8px" }}>
                          <strong>Note:</strong>
                          <p style={{ margin: "5px 0", lineHeight: "1.4" }}>{note.note}</p>
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          <span style={{ marginRight: "15px" }}>
                            <strong>Created:</strong> {note.created_at}
                          </span>
                          <span>
                            <strong>By:</strong> {note.created_by}
                          </span>
                        </div>
                      </div>
                      <div style={{ marginLeft: "10px" }}>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <Button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            style={{
                              backgroundColor: "#007bff",
                              borderColor: "#007bff",
                              color: "white"
                            }}
                            onClick={() => {
                              setEditingNoteIndex(index);
                              setEditedNoteText(note.note);
                              setOriginalNoteContent(note.note); // Store original content
                            }}
                          >
                            Edit
                          </Button>
                          <span
                            className={'k-icon k-i-trash'}
                            style={{ 
                              color: '#dc3545', 
                              fontSize: '16px', 
                              cursor: 'pointer',
                              padding: '8px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            onClick={() => {
                              handleDeleteDealerNote(note.created_at, note.note, index);
                            }}
                            title="Delete note"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>No notes available.</p>
            )}
          </div>
          <DialogActionsBar>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => {
                setOutreachNotesModalVisible(false);
                setShowAddNoteInModal(false);
                setNewOutreachNoteTextInModal('');
              }}
            >
              Close
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Add Dealer Note Modal */}
      {addDealerNoteModalVisible && (
        <Dialog
          title="Add Dealer Note"
          onClose={() => setAddDealerNoteModalVisible(false)}
          width={500}
          height={300}
        >
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: 15 }}>
              <strong>Dealer: {selectedDealerNameForNote}</strong>
            </div>
            <div style={{ marginBottom: 15 }}>
              <label>Note:</label>
              <textarea
                value={newDealerNoteText}
                onChange={(e) => setNewDealerNoteText(e.target.value)}
                placeholder="Enter dealer note..."
                style={{
                  width: '100%',
                  height: '100px',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  marginTop: '5px'
                }}
              />
            </div>
            {errorMessage && (
              <div style={{ color: 'red', fontSize: 12, marginBottom: 10 }}>
                {errorMessage}
              </div>
            )}
          </div>
          <DialogActionsBar>
            <Button
              primary={true}
                                          onClick={handleSaveDealerNote}
              disabled={!newDealerNoteText.trim()}
            >
              Save Note
            </Button>
            <Button onClick={() => setAddDealerNoteModalVisible(false)}>
              Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Banking Info Dialog */}
      {memoDialogVisible && (
        <Dialog
          title={preApprovalMemo || preApprovalCheckPayable ? 'Edit Banking Info' : 'Add Banking Info'}
          onClose={() => setMemoDialogVisible(false)}
          width={480}
        >
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>
                Check Payable
              </label>
              <Input
                value={checkPayableText}
                onChange={(e) => setCheckPayableText(e.value as string)}
                style={{ width: '100%' }}
                placeholder="Enter check payable..."
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>
                Memo
              </label>
              <TextArea
                value={memoText}
                onChange={(e) => setMemoText(e.value as string)}
                rows={4}
                style={{ width: '100%' }}
                placeholder="Enter memo..."
              />
            </div>
          </div>
          <DialogActionsBar>
            <Button
              primary={true}
              onClick={handleSaveMemo}
              disabled={isLoading}
            >
              Save
            </Button>
            <Button onClick={() => setMemoDialogVisible(false)}>
              Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* Dealer Notes Modal */}
      {dealerNotesModalVisible && (
        <Dialog
          title={`Dealer Notes - ${selectedDealerNameForNotes}`}
          onClose={() => {
            setDealerNotesModalVisible(false);
            setShowAddDealerNoteInModal(false);
            setNewDealerNoteTextInModal('');
            setDealerNotesModalDealerId(null);
          }}
          width={600}
          height={showAddDealerNoteInModal ? 550 : 400}
        >
          <div style={{ padding: "20px" }}>
            {/* Add Note Section - Moved to top */}
            {showAddDealerNoteInModal ? (
              <div style={{ 
                marginBottom: "20px", 
                padding: "15px", 
                border: "1px solid #007bff", 
                borderRadius: "5px",
                backgroundColor: "#f8f9fa"
              }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                  Add New Dealer Note:
                </label>
                <textarea
                  value={newDealerNoteTextInModal}
                  onChange={(e) => setNewDealerNoteTextInModal(e.target.value)}
                  placeholder="Enter your dealer note here..."
                  style={{
                    width: "100%",
                    height: "80px",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    resize: "vertical",
                    boxSizing: "border-box"
                  }}
                />
                <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                  <Button
                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                    style={{
                      backgroundColor: "#007bff",
                      borderColor: "#007bff",
                      color: "white"
                    }}
                    onClick={handleSaveDealerNoteInModal}
                    disabled={!newDealerNoteTextInModal.trim()}
                  >
                    Save Note
                  </Button>
                  <Button
                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                    onClick={() => {
                      setShowAddDealerNoteInModal(false);
                      setNewDealerNoteTextInModal('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                style={{ 
                  marginBottom: "20px",
                  backgroundColor: "#007bff",
                  borderColor: "#007bff"
                }}
                onClick={() => setShowAddDealerNoteInModal(true)}
              >
                Add Note
              </Button>
            )}

            {selectedDealerNotes.length > 0 ? (
              selectedDealerNotes.map((note, index) => (
                <div 
                  key={index} 
                  style={{ 
                    marginBottom: "20px", 
                    padding: "15px", 
                    border: "1px solid #ddd", 
                    borderRadius: "5px",
                    backgroundColor: "#f9f9f9"
                  }}
                >
                  {editingDealerNoteIndex === index ? (
                    // Edit mode - stack everything vertically
                    <div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Note:</strong>
                        <textarea
                          value={editedDealerNoteText}
                          onChange={(e) => setEditedDealerNoteText(e.target.value)}
                          style={{
                            width: "100%",
                            height: "80px",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            marginTop: "5px",
                            boxSizing: "border-box"
                          }}
                        />
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
                        <span style={{ marginRight: "15px" }}>
                          <strong>Created:</strong> {note.created_at}
                        </span>
                        <span>
                          <strong>By:</strong> {note.created_by}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <Button
                          className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                          style={{
                            backgroundColor: "#007bff",
                            borderColor: "#007bff",
                            color: "white"
                          }}
                          onClick={() => handleSaveEditedDealerNote(note.created_at)}
                          disabled={!editedDealerNoteText.trim()}
                        >
                          Save
                        </Button>
                        <Button
                          className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                          onClick={() => {
                            setEditingDealerNoteIndex(null);
                            setEditedDealerNoteText('');
                            setOriginalDealerNoteContent('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode - keep original layout
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: "8px" }}>
                          <strong>Note:</strong>
                          <p style={{ margin: "5px 0", lineHeight: "1.4" }}>{note.note}</p>
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          <span style={{ marginRight: "15px" }}>
                            <strong>Created:</strong> {note.created_at}
                          </span>
                          <span>
                            <strong>By:</strong> {note.created_by}
                          </span>
                        </div>
                      </div>
                      <div style={{ marginLeft: "10px" }}>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <Button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            style={{
                              backgroundColor: "#007bff",
                              borderColor: "#007bff",
                              color: "white"
                            }}
                            onClick={() => {
                              setEditingDealerNoteIndex(index);
                              setEditedDealerNoteText(note.note);
                              setOriginalDealerNoteContent(note.note);
                            }}
                          >
                            Edit
                          </Button>
                          <span
                            className={'k-icon k-i-trash'}
                            style={{ 
                              color: '#dc3545', 
                              fontSize: '16px', 
                              cursor: 'pointer',
                              padding: '8px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            onClick={() => {
                              handleDeleteDealerNote(note.created_at, note.note, index);
                            }}
                            title="Delete note"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>No dealer notes available.</p>
            )}
          </div>
          <DialogActionsBar>
            <Button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={() => {
                setDealerNotesModalVisible(false);
                setShowAddDealerNoteInModal(false);
                setNewDealerNoteTextInModal('');
                setDealerNotesModalDealerId(null);
              }}
            >
              Close
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default EventDetailScreen;
